import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { fmtErr } from "@/lib/utils";
import { success as successHaptic } from "@/lib/haptics";
import { toast } from "sonner";
import { Camera as CapCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import { GlobeHemisphereWest, ForkKnife, Fire, MapPin, Camera, ClipboardText, ListBullets, ShareNetwork, ArrowLeft, ShieldWarning, Check, X, Warning, TextAa, CaretLeft, CaretRight, ArrowClockwise, Flag, Sparkle, SealCheck, SmileyMeh, Crown, HandHeart, Sunglasses, Plus, Bell } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import type { FoodItem } from "@/components/FoodCard";
import { schedulePostMealNotification } from "@/lib/mealNotification";
import { AnticipationShareCard, type MoodTag } from "@/components/ShareCards";
import AllergySettings, {
  loadSettings,
  saveSettings,
  type UserSettings,
} from "@/components/AllergySettings";
import AllergyFilterBar from "@/components/AllergyFilterBar";
import BottomNav from "@/components/BottomNav";
import RatingPrompt from "@/components/RatingPrompt";
import {
  loadDishes,
  saveDishes,
  saveMenu,
  updateMenu,
  loadMenus,
  generateId,
  getCurrentLocation,
  reverseGeocode,
  addPendingRatings,
  getUnratedDishesAfterOneHour,
  markPendingAsPrompted,
  updateDish,
  type SavedDish,
  type PendingRating,
} from "@/lib/storage";
import { getCachedImage, getCachedImageById, cacheImage, queueImageGeneration, getCachedImageLocal, getCachedImageByItemKey, cacheImageByItemKey, warmVisibleItems, warmVisibleItemsServerSide, clearCachedImage, computeMenuFingerprint, checkMenuFingerprint, storeMenuFingerprint, compressAndStoreInDB, canonicalDishName } from "@/lib/imageCache";
import { analyzeMenuDirect, analyzeMenuStreaming, enrichMenuItems } from "@/lib/menuAnalyzer";
import { generateFoodImageDirect } from "@/lib/openrouterImage";
import { track } from "@/lib/analytics";
import { DidYouKnowFact } from "@/components/DidYouKnowFact";
import { CategoryPlaceholder } from "@/components/CategoryPlaceholder";





// storyCard imported dynamically to avoid circular TDZ
// shareTargets imported dynamically to avoid TDZ
type ShareMode = "experience-card" | "story-set" | "photo-card" | "photo-cards" | "card-only" | "selected-cards";
type ShareTone = "fire" | "mid" | "vibe" | "honest" | "elite" | "cozy" | "lowkey";
const SHARE_TONES: { id: ShareTone; label: string; icon: typeof Fire; color: string; caption: (restaurant: string) => string; shareText: (restaurant: string) => string }[] = [
  { id: "fire", label: "fire", icon: Fire, color: "#EF4444", caption: (r) => `${r} came through`, shareText: (r) => `just yumzed ${r} and it did NOT disappoint 🔥` },
  { id: "vibe", label: "vibes", icon: Sparkle, color: "#A855F7", caption: (r) => `${r} was all vibes`, shareText: (r) => `${r} was giving main character energy ✨` },
  { id: "honest", label: "honest", icon: SealCheck, color: "#3B82F6", caption: (r) => `real talk: ${r} was solid`, shareText: (r) => `gave ${r} the honest review it deserved 💯` },
  { id: "mid", label: "mid", icon: SmileyMeh, color: "#6B7280", caption: (r) => `${r} was mid ngl`, shareText: (r) => `${r}... it was giving mid energy 😐` },
  { id: "elite", label: "elite", icon: Crown, color: "#F59E0B", caption: (r) => `${r} came correct`, shareText: (r) => `${r} just entered the elite tier 👑` },
  { id: "cozy", label: "cozy", icon: HandHeart, color: "#EC4899", caption: (r) => `${r} felt like home`, shareText: (r) => `${r} was the comfort we needed 🫶` },
  { id: "lowkey", label: "lowkey", icon: Sunglasses, color: "#818CF8", caption: (r) => `${r} was lowkey the move`, shareText: (r) => `lowkey ${r} might be underrated 😎` },
];

type ViewState = "home" | "analyzing" | "collecting" | "preparing" | "results" | "sharing" | "waiter" | "easy-read";
type SortMode = "menu" | "smart" | "protein" | "low-cal" | "light" | "tiny" | "low-sugar";

/** Convert a data:image/… base64 URL to a lightweight blob: object URL for React state */
function toBlobUrl(dataUrl: string | null | undefined): string | undefined {
  if (!dataUrl) return undefined;
  if (!dataUrl.startsWith("data:")) return dataUrl; // already a URL
  try {
    const [header, b64] = dataUrl.split(",");
    const mime = header.match(/data:(.*?);/)?.[1] || "image/png";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: mime }));
  } catch {
    return dataUrl; // fallback
  }
}

/** Hash a menu image by downscaling to 32x32 grayscale and using SHA-256 */
async function hashMenuImage(base64: string): Promise<string | null> {
  try {
    const img = new Image();
    const loaded = new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; });
    img.src = `data:image/jpeg;base64,${base64}`;
    await loaded;
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, 32, 32);
    const pixels = ctx.getImageData(0, 0, 32, 32).data;
    // Convert to grayscale bytes
    const gray = new Uint8Array(32 * 32);
    for (let i = 0; i < 32 * 32; i++) {
      gray[i] = Math.round(pixels[i * 4] * 0.299 + pixels[i * 4 + 1] * 0.587 + pixels[i * 4 + 2] * 0.114);
    }
    const hashBuffer = await crypto.subtle.digest("SHA-256", gray);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

const getItemCalories = (item: any) => item?.nutrition?.calories ?? ((item?.nutrition?.protein != null || item?.nutrition?.carbs != null || item?.nutrition?.fat != null) ? Math.round((item?.nutrition?.protein || 0) * 4 + (item?.nutrition?.carbs || 0) * 4 + (item?.nutrition?.fat || 0) * 9) : null);

const looksLikeDrink = (item: any) => {
  const nameText = `${item?.name || ""} ${item?.originalName || ""}`.toLowerCase();
  const descText = `${item?.description || ""}`.toLowerCase();
  // Food indicators — if ANY of these are in the name, it's NOT a drink
  const foodIndicators = /(chicken|steak|burger|sandwich|salad|wings|fries|pasta|pizza|soup|ribs|tacos|shrimp|fish|pork|beef|lamb|nachos|quesadilla|wrap|bowl|platter|appetizer|entree|dessert|cake|pie|brownie|bread|rice|grilled|fried|baked|roasted|pretzel|cheese|dip|mozzarella|onion|ring|nugget|tender|slider|bites|poppers|flatbread|bruschetta|hummus|guacamole|artichoke|seafood|lobster|crab|salmon|tuna|cod|halloumi|fillet|filet|ribeye|sirloin|entrecote|entrecôte|wagyu|tartare|oyster|branzino|calamari|scallop|mussel|clam|prawn)/i;
  if (foodIndicators.test(nameText)) return false;
  // Check for the item's category from the menu parser
  const cat = (item?.category || "").toLowerCase();
  if (cat && /(drink|beverage|cocktail|wine|beer|coffee|tea|juice|soda|bar|bottle)/.test(cat)) return true;
  if (cat && /(appetizer|entree|main|starter|side|dessert|salad|soup|grill|seafood|pasta|pizza|sandwich|burger)/.test(cat)) return false;
  const strongName = /(champagne|cava|prosecco|\bwine\b|\bbeer\b|lager|\bipa\b|\bale\b|negroni|martini|cocktail|spritz|whiskey|whisky|vodka|gin(?!ger)|\brum\b|tequila|mezcal|sauvignon|chardonnay|pinot|merlot|cabernet|aperol|sangria|rosé|riesling|malbec|syrah|shiraz|tempranillo|grenache|\bjuice\b|lemonade|\bsoda\b|\bcola\b|\bwater\b|\bcoffee\b|espresso|cappuccino|\blatte\b|\btea\b(?!k)|smoothie|milkshake|mocktail|\bmilk\b|by the glass)/.test(nameText);
  const strongDesc = /(cocktail|champagne|cava|\bwine\b|\bbeer\b|by the glass|glass of|sparkling wine|\bsoda\b|\bjuice\b|lemonade|\bcola\b|\bcoffee\b|espresso|cappuccino|\blatte\b|\btea\b(?!k)|smoothie|milkshake|\bmilk\b)/.test(descText);
  return strongName || strongDesc;
};

const sanitizeDisplayText = (text?: string, lang?: string) => {
  const value = (text || "").trim();
  if (!value) return value;
  const keepNonLatin = /^(zh|ja|ko|th|ar|he)/i.test(lang || "");
  if (keepNonLatin) return value;
  return value
    .replace(/[\u4E00-\u9FFF\u3400-\u4DBF\u3040-\u30FF\uAC00-\uD7AF]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const stableDishKey = (item: any) => `${item?.originalName || item?.name || "dish"} ${item?.name || ""}`.toLowerCase().replace(/\s+/g, ' ').trim();
const visibleDishKey = (item: any) => `${item?.name || "dish"}`.toLowerCase().replace(/\s+/g, ' ').trim();
const makeItemImageKey = (_restaurantName: string, item: any) =>
  `${stableDishKey(item)}::${item?.price || ""}`.toLowerCase().trim();
const makeItemImageAliases = (restaurantName: string, item: any) => ([
  makeItemImageKey(restaurantName, item),
  `${visibleDishKey(item)}::${item?.price || ""}`.toLowerCase().trim(),
  visibleDishKey(item),
]).filter(Boolean);

const findPriorMenuImage = (restaurantName: string, item: any): string | undefined => {
  const targetKey = makeItemImageKey(restaurantName, item);
  const menus = loadMenus();
  for (const menu of menus) {
    for (const prev of menu.items || []) {
      if (makeItemImageKey(menu.restaurantName || restaurantName, prev) === targetKey && prev.imageUrl) {
        return prev.imageUrl;
      }
    }
  }
  return undefined;
};

type RankedItem = { item: FoodItem; originalIndex: number };
type RankedSections = { main: RankedItem[]; drinks: RankedItem[] };

/**
 * Sanitize a restaurant name coming back from OCR. The vision model
 * sometimes echoes the JSON key ("name"), returns a literal "null"/"unknown"
 * string, or hands back whitespace/placeholder junk when it can't read the
 * name off the menu. Any of those would otherwise populate the input and
 * hide the "type restaurant name here" placeholder. Returns "" for junk so
 * the field stays empty and the placeholder shows.
 */
const cleanRestaurantName = (raw: unknown): string => {
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const junk = new Set(["name", "null", "undefined", "unknown", "n/a", "na", "none", "restaurant", "restaurant name", "menu"]);
  if (junk.has(trimmed.toLowerCase())) return "";
  return trimmed;
};

const textOfItem = (item: FoodItem) => `${item.name} ${item.description || ""} ${(item.dietaryTags || []).join(" ")}`.toLowerCase();
const hasAnyText = (item: FoodItem, words: string[]) => words.some((w) => textOfItem(item).includes(w));
const isDrinkItem = (item: FoodItem) => looksLikeDrink(item);
const isDessertItem = (item: FoodItem) => hasAnyText(item, ["dessert","cake","ice cream","sorbet","chocolate","sweet","caramel","brownie","tiramisu","panna cotta"]);
const isLeanProteinItem = (item: FoodItem) => hasAnyText(item, ["grilled", "roasted", "seared", "steamed", "fish", "salmon", "tuna", "cod", "chicken", "turkey", "shrimp", "prawn", "tofu", "egg", "steak", "beef", "entrecôte", "entrecote", "ribeye", "sirloin", "fillet", "filet"]);
const isHeavyItem = (item: FoodItem) => hasAnyText(item, ["fried", "crispy", "cream", "creamy", "butter", "cheese", "aioli", "mayo", "pasta", "risotto", "burger", "fries", "potato", "bread", "brioche"]);
const isSmallPlateItem = (item: FoodItem) => hasAnyText(item, ["small", "snack", "side", "starter", "bite", "mini", "tapas", "olives", "almonds", "bread"]);
const caloriesItem = (item: FoodItem) => item.nutrition?.calories ?? ((item.nutrition?.protein != null || item.nutrition?.carbs != null || item.nutrition?.fat != null) ? Math.round((item.nutrition?.protein || 0) * 4 + (item.nutrition?.carbs || 0) * 4 + (item.nutrition?.fat || 0) * 9) : null);
const proteinItem = (item: FoodItem) => item.nutrition?.protein ?? (hasAnyText(item, ["steak", "beef", "chicken", "fish", "salmon", "tuna", "cod", "shrimp", "prawn", "egg", "tofu", "entrecôte", "entrecote", "ribeye", "sirloin", "fillet", "filet", "tenderloin", "veal", "lamb chops"]) ? 32 : hasAnyText(item, ["halloumi", "lentil", "bean", "chickpea"]) ? 14 : 0);
const fatItem = (item: FoodItem) => item.nutrition?.fat ?? (hasAnyText(item, ["fried", "cream", "butter", "cheese", "aioli", "mayo", "bearnaise", "béarnaise", "truffle butter"]) ? 28 : 0);
const scoreGymItem = (item: FoodItem) => proteinItem(item) * 4 + (isLeanProteinItem(item) ? 20 : 0) - (caloriesItem(item) || 650) / 60 - (isDessertItem(item) ? 30 : 0) - (isDrinkItem(item) ? 35 : 0);
const scoreCutItem = (item: FoodItem) => {
  const c = caloriesItem(item) ?? (isHeavyItem(item) ? 850 : isSmallPlateItem(item) ? 350 : 650);
  return 1000 - c + proteinItem(item) * 6 - fatItem(item) * 5 + (isLeanProteinItem(item) ? 80 : 0) - (isDessertItem(item) ? 220 : 0) - (isDrinkItem(item) ? 160 : 0) - (isHeavyItem(item) ? 90 : 0);
};
const scoreMainCharacterItem = (item: FoodItem) => {
  const t = textOfItem(item);
  const premium = ["truffle", "wagyu", "tartare", "oyster", "lobster", "caviar", "entrecôte", "entrecote", "ribeye", "branzino", "signature", "chef"];
  return premium.reduce((s, w) => s + (t.includes(w) ? 30 : 0), 0) + (item.confidence || 0) + (item.imageUrl ? 12 : 0) - (isDrinkItem(item) ? 40 : 0);
};
const scoreTinyItem = (item: FoodItem) => (isSmallPlateItem(item) ? 100 : 0) + (caloriesItem(item) ? Math.max(0, 550 - (caloriesItem(item) || 0)) / 4 : 20) - (isHeavyItem(item) ? 25 : 0);
const scoreSugarItem = (item: FoodItem) => (isDessertItem(item) ? -200 : 80) + scoreCutItem(item) / 20;
const scoreSmartItem = (item: FoodItem) => scoreMainCharacterItem(item) + scoreCutItem(item) / 12 + scoreGymItem(item) / 3;

const rankMenuItems = (
  sourceItems: FoodItem[],
  mode: SortMode,
  hasAllergenMatchFn?: (item: FoodItem) => boolean,
  allergyFilter: "all" | "safe" | "warnings" = "all",
): RankedSections => {
  const filtered = allergyFilter === "all"
    ? sourceItems
    : allergyFilter === "safe"
    ? sourceItems.filter((it) => it.sectionHeader || !hasAllergenMatchFn?.(it))
    : sourceItems.filter((it) => it.sectionHeader || hasAllergenMatchFn?.(it));
  const baseItems = filtered
    .filter((item) => !item.sectionHeader && !isDrinkItem(item))
    .map((item) => ({ item, originalIndex: sourceItems.indexOf(item) }));
  const readyBoost = (x: RankedItem) => (x.item.imageUrl && !x.item.imageLoading ? 0.001 : 0);
  const toHeader = (name: string): RankedItem => ({ item: { name, description: "", sectionHeader: true } as FoodItem, originalIndex: -1 });
  const sectioned = (goodTitle: string, avoidTitle: string, score: (item: FoodItem) => number, goodTest?: (item: FoodItem) => boolean) => {
    const ranked = [...baseItems].sort((a,b) => (score(b.item) + readyBoost(b)) - (score(a.item) + readyBoost(a)));
    const cutoff = Math.max(2, Math.ceil(ranked.length * 0.45));
    const good = ranked.filter(x => goodTest ? goodTest(x.item) : score(x.item) >= 0);
    const top = good.length ? ranked.filter(x => goodTest ? goodTest(x.item) : score(x.item) >= 0).slice(0, cutoff) : ranked.slice(0, cutoff);
    const bottom = ranked.filter(x => !top.includes(x));
    const drinks = filtered.filter((item) => !item.sectionHeader && isDrinkItem(item)).map((item) => ({ item, originalIndex: sourceItems.indexOf(item) }));
    return {
      main: [toHeader(goodTitle), ...top, ...(bottom.length ? [toHeader(avoidTitle), ...bottom] : [])],
      drinks,
    };
  };

  if (mode === "menu") {
    return {
      main: filtered.filter((item) => !item.sectionHeader).map((item) => ({ item, originalIndex: sourceItems.indexOf(item) })),
      drinks: [],
    };
  }

  switch (mode) {
    case "protein":
      return sectioned("gym mode picks", "not enough protein", scoreGymItem, (it) => (proteinItem(it) >= 18 || isLeanProteinItem(it)) && !isDessertItem(it) && !isDrinkItem(it));
    case "low-cal":
      return sectioned("cut mode picks", "not really cut mode", scoreCutItem, (it) => scoreCutItem(it) > 500);
    case "light":
      return sectioned("main character picks", "supporting cast", scoreMainCharacterItem, (it) => scoreMainCharacterItem(it) >= 35);
    case "tiny":
      return sectioned("just a lil one", "bigger commitment", scoreTinyItem, (it) => scoreTinyItem(it) >= 45);
    case "low-sugar":
      return sectioned("sweet skip picks", "sugar crash zone", scoreSugarItem, (it) => !isDessertItem(it));
    default:
      return sectioned("for you picks", "maybe skip", scoreSmartItem, (it) => scoreSmartItem(it) >= 60);
  }
};

const initialVisibleFoodTargets = (sourceItems: FoodItem[], count = 8): { item: FoodItem; originalIndex: number }[] => {
  // Warm first N food items in menu order (array order)
  const result: { item: FoodItem; originalIndex: number }[] = [];
  for (let i = 0; i < sourceItems.length && result.length < count; i++) {
    const item = sourceItems[i];
    if (!item.sectionHeader && !isDrinkItem(item)) {
      result.push({ item, originalIndex: i });
    }
  }
  return result;
};

const Index = () => {
  const [viewState, setViewState] = useState<ViewState>("home");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [totalImagesToLoad, setTotalImagesToLoad] = useState(0);
  // Separate image URL map — avoids stale closure issues with setItems
  const [imageMap, setImageMap] = useState<Record<number, string>>({});
  const showResultsRef = useRef(false);
  // Wave M4 — gate the results reveal until enough images are ready, so
  // the user doesn't land on an empty grid that fills in over time.
  const revealedRef = useRef(false);
  const [prepareReady, setPrepareReady] = useState(0); // images ready while on the "preparing" screen
  const [prepareTarget, setPrepareTarget] = useState(0); // flip prepareComplete when prepareReady hits this (or 20s)
  // Wave T — single-screen flow: instead of auto-jumping to results when
  // enough images load, flip prepareComplete=true and let the user tap
  // "let's eat" on the same surface. Makes the loading → results feel like
  // ONE transition rather than two separate screens.
  const [prepareComplete, setPrepareComplete] = useState(false);
  const [items, setItems] = useState<FoodItem[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [detectedLanguage, setDetectedLanguage] = useState<string | undefined>();
  const [menuType, setMenuType] = useState<string | undefined>();
  const [averageConfidence, setAverageConfidence] = useState<number | null>(null);
  const [settings, setSettings] = useState<UserSettings>(loadSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantNameInput, setRestaurantNameInput] = useState("");
  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
    label?: string;
  } | null>(null);
  const [allergyFilter, setAllergyFilter] = useState<
    "all" | "safe" | "warnings"
  >("all");
  const [ratingDishes, setRatingDishes] = useState<PendingRating[]>([]);
  const [showRating, setShowRating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [restaurantConfirmed, setRestaurantConfirmed] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(false);
  const [sharingDishes, setSharingDishes] = useState<Array<{ name: string; imageUrl?: string; price?: string; description?: string }>>([])
  const [sharingRestaurant, setSharingRestaurant] = useState("");
  const [sharingLocation, setSharingLocation] = useState<string | undefined>();
  const [groupPhoto, setGroupPhoto] = useState<string | null>(null);
  const [sharingPlatform, setSharingPlatform] = useState<string | null>(null);
  const [shareMode, setShareMode] = useState<ShareMode>("experience-card");
  const [shareTone, setShareTone] = useState<ShareTone>("fire");
  const [showHome, setShowHome] = useState(false);
  const [currentMenuId, setCurrentMenuId] = useState<string | null>(null);
  const [noMenuDetected, setNoMenuDetected] = useState(false);
  const [dishMatch, setDishMatch] = useState<{
    dishName: string;
    matchedRestaurant?: string;
    matchedDishName?: string;
    isFood: boolean;
    photoBase64: string;
  } | null>(null);
  const [identifyingDish, setIdentifyingDish] = useState(false);
  const [sharePreviewUrl, setSharePreviewUrl] = useState<string | null>(null);
  const [sharePreviewLoading, setSharePreviewLoading] = useState(false);
  const [easyReadIndex, setEasyReadIndex] = useState(0);
  const [sortMode, setSortMode] = useState<SortMode>("menu");
  const [waiterVisibleCount, setWaiterVisibleCount] = useState(6);
  const [pageCount, setPageCount] = useState(0);
  const [orderedDishIds, setOrderedDishIds] = useState<Set<string>>(new Set());
  const [showAnticipationShare, setShowAnticipationShare] = useState(false);
  const isAppendModeRef = useRef(false);
  const [pagesProcessing, setPagesProcessing] = useState(0);
  const [pagesCompleted, setPagesCompleted] = useState(0);
  const pendingItemsRef = useRef<FoodItem[]>([]);
  const easyReadTouchStartX = useRef<number | null>(null);

  // Live camera viewfinder state
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const cameraRef = useRef<HTMLInputElement>(null);
  const [indexError, setIndexError] = useState<string | null>(null);
  // Image generation tracking
  const imageSetRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handler = (e: ErrorEvent) => {
      if (e.error) {
        setIndexError(e.error.message + ' :: ' + (e.error.stack || '').split('\n')[1]);
      }
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);
  const groupCameraRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [notifUnread, setNotifUnread] = useState(0);

  // Refresh unread count when returning to home
  useEffect(() => {
    import("@/lib/notifications").then(m => setNotifUnread(m.unreadCount()));
  }, [viewState]);

  useEffect(() => {
    const root = document.getElementById("root");
    const shouldLockScroll = viewState === "sharing" || viewState === "waiter" || (viewState === "home" && !showHome);

    if (root) {
      root.style.overflowY = shouldLockScroll ? "hidden" : "auto";
      root.style.overscrollBehaviorY = shouldLockScroll ? "none" : "auto";
    }

    return () => {
      if (root) {
        root.style.overflowY = "auto";
        root.style.overscrollBehaviorY = "none";
      }
    };
  }, [viewState, showHome]);

  // Wave L4 hot-fix — L4-B IntersectionObserver removed (eager gen
  // is the right behavior; lazy gen made perceived load slower).

  // Start/stop live camera based on view state + app lifecycle
  useEffect(() => {
    let stream: MediaStream | null = null;
    let mounted = true;

    const startCamera = async () => {
      try {
        setCameraError(false);
        setCameraReady(false);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
            if (mounted) setCameraReady(true);
          };
        }
      } catch (_err) {
        if (mounted) {
          setCameraError(true);
          setCameraStream(null);
        }
      }
    };

    const stopCamera = () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach((t) => t.stop());
      }
      setCameraStream(null);
      setCameraReady(false);
    };

    // Restart camera when app resumes from background
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && viewState === "home" && !showHome) {
        stopCamera();
        setTimeout(() => startCamera(), 300);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Only run camera when on the camera-first home view
    if (viewState === "home" && !showHome) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, showHome]);

  // Capture frame from live viewfinder
  // Capture: grab frame directly from live viewfinder (no native camera popup)
  const captureFrame = useCallback(async () => {
    // Guard: if camera isn't ready yet, show feedback
    if (!cameraStream && !videoRef.current?.srcObject) {
      toast.info("Camera is starting up — try again in a moment", { duration: 2000 });
      return;
    }

    // Method 1: ImageCapture API (best for iOS)
    if (cameraStream) {
      try {
        const track = cameraStream.getVideoTracks()[0];
        // @ts-ignore - ImageCapture may not be in all TS defs
        if (typeof ImageCapture !== 'undefined') {
          // @ts-ignore
          const capture = new ImageCapture(track);
          const blob = await capture.takePhoto({ imageWidth: 1920 });
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = (reader.result as string).split(',')[1];
            if (base64 && base64.length > 100) {
              handleImageSelected(base64);
              return;
            }
          };
          reader.readAsDataURL(blob);
          return;
        }
      } catch (_e) {
        // ImageCapture not supported, try canvas
      }
    }

    // Method 2: Canvas capture from video element
    if (videoRef.current && canvasRef.current) {
      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth || 1920;
        canvas.height = video.videoHeight || 1080;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = (reader.result as string).split(',')[1];
                if (base64 && base64.length > 100) {
                  handleImageSelected(base64);
                }
              };
              reader.readAsDataURL(blob);
            }
          }, 'image/jpeg', 0.85);
          return;
        }
      } catch (_e) {
        // Canvas failed
      }
    }

    // Method 3: Capacitor Camera plugin as last resort
    try {
      const photo = await CapCamera.getPhoto({
        quality: 85,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        width: 1920,
        correctOrientation: true,
      });
      if (photo.base64String) {
        handleImageSelected(photo.base64String);
      }
    } catch (_e) {
      // All methods failed — tell the user
      toast.error("Couldn't capture photo. Try tapping again or use the photo library.", { duration: 3000 });
      cameraRef.current?.click();
    }
  }, [cameraStream]);

  // Try to identify a dish photo and match to recent menus
  const tryIdentifyDish = async (base64: string) => {
    setIdentifyingDish(true);
    setViewState("home");
    setShowHome(false);
    setSharingDishes([]);
    setSharingRestaurant("");
    try {
      const recentMenus = loadMenus().slice(0, 5).map((m) => ({
        restaurantName: m.restaurantName,
        date: m.date,
        items: m.items.map((it) => ({ name: it.name })),
      }));

      const { data, error } = await supabase.functions.invoke("identify-dish", {
        body: { image: base64, recentMenus },
      });

      if (error || !data?.isFood) {
        track("menu_parse_failed");
        setNoMenuDetected(true);
        setIdentifyingDish(false);
        return;
      }

      setDishMatch({
        dishName: data.dishName,
        matchedRestaurant: data.matchedRestaurant || undefined,
        matchedDishName: data.matchedDishName || undefined,
        isFood: data.isFood,
        photoBase64: base64,
      });
      setIdentifyingDish(false);
    } catch (_e) {
      setNoMenuDetected(true);
      setIdentifyingDish(false);
    }
  };

  // Save identified dish to faves
  const handleSaveDishMatch = () => {
    if (!dishMatch) return;
    const menuId = dishMatch.matchedRestaurant
      ? loadMenus().find((m) => m.restaurantName === dishMatch.matchedRestaurant)?.id
      : undefined;
    const newDish: SavedDish = {
      id: generateId("dish"),
      menuId: menuId || "standalone",
      name: dishMatch.matchedDishName || dishMatch.dishName,
      description: "",
      restaurantName: dishMatch.matchedRestaurant || "Unknown",
      orderedAt: new Date().toISOString(),
      imageUrl: `data:image/jpeg;base64,${dishMatch.photoBase64}`,
    };
    saveDishes([newDish]);
    toast.success(`${newDish.name} added to faves! \uD83D\uDD25`);
    setDishMatch(null);

  };



  // Generate share card preview when sharing view is active or when mode/tone changes
  useEffect(() => {
    if (viewState !== "sharing") return;
    let cancelled = false;

    const generate = async () => {
      setSharePreviewLoading(true);
      setSharePreviewUrl(null);
      try {
        let blob: Blob;
        if (shareMode === "experience-card") {
          blob = await (await import("@/lib/storyCard")).generateExperienceCard({
            restaurantName: sharingRestaurant,
            locationLabel: sharingLocation,
            items: sharingDishes,
            tone: shareTone,
          });
        } else {
          // Photo Card or any other mode — photo-led
          blob = await (await import("@/lib/storyCard")).generateStoryCard({
            photoUrl: groupPhoto || sharingDishes[0]?.imageUrl || "",
            dishName: sharingDishes[0]?.name || "Your order",
            restaurantName: sharingRestaurant,
            selectedDishes: sharingDishes,
            groupPhotoUrl: groupPhoto || undefined,
            tone: shareTone,
          });
        }
        if (!cancelled) {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (!cancelled) setSharePreviewUrl(reader.result as string);
          };
          reader.readAsDataURL(blob);
        }
      } catch (_e) {
        // preview failed silently
      } finally {
        if (!cancelled) setSharePreviewLoading(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, shareMode, shareTone, groupPhoto]);

  const handleSettingsSave = (newSettings: UserSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
    toast.success("Allergies saved!");
  };

  const triggerCamera = useCallback(() => {
    cameraRef.current?.click();
  }, []);

  const handleCameraFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      handleImageSelected(base64);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleImageSelected = async (base64: string) => {
    const appendMode = isAppendModeRef.current;
    isAppendModeRef.current = false;

    if (!appendMode) {
      // Fresh scan — reset everything
      setImagesLoaded(0);
      setTotalImagesToLoad(0);
      setItems([]); setImageMap({}); setFlaggedIndices(new Set());
      setSelectedIndices(new Set());
      imageSetRef.current.clear();
      setDetectedLanguage(undefined);
      setMenuType(undefined);
      setAverageConfidence(null);
      setRestaurantName("");
      setRestaurantNameInput("");
      setLocation(null);
      setRestaurantConfirmed(false);
      setEditingRestaurant(false);
      setCurrentMenuId(null);
      setPageCount(0);
      setPagesProcessing(0);
      setPagesCompleted(0);
      pendingItemsRef.current = [];

      // Capture location in background
      getCurrentLocation()
        .then(async (loc) => {
          const label = await reverseGeocode(loc.lat, loc.lng);
          setLocation({ ...loc, label });
        })
        .catch((_e) => {});
    }

    // Go to collecting view immediately — don't make user wait
    setViewState("collecting");
    setPagesProcessing(prev => prev + 1);
    showResultsRef.current = false;

    try {
      // Hash the image and check Supabase menu cache
      const imageHash = await hashMenuImage(base64);
      let data: any = null;
      let cacheHit = false;

      if (imageHash) {
        try {
          const anonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRuc3FtcHN4enZkeXh2dHVpZW50Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU4MTMyNzgsImV4cCI6MjA5MTM4OTI3OH0.xY5R4ow8QEHkQ4aAfRUt359iHemxM_PTa5fr5k-8VO0";
          const cacheResp = await fetch(
            `https://tnsqmpsxzvdyxvtuient.supabase.co/rest/v1/menu_cache?image_hash=eq.${imageHash}&select=parsed_result,hit_count&limit=1`,
            { headers: { "apikey": anonKey, "Authorization": `Bearer ${anonKey}` } }
          );
          const rows = await cacheResp.json();
          const cached = rows?.[0];
          if (cached?.parsed_result) {
            console.log(`[menuCache] HIT for hash ${imageHash.substring(0, 12)}...`);
            data = cached.parsed_result;
            cacheHit = true;
            // Update hit count in background
            fetch(
              `https://tnsqmpsxzvdyxvtuient.supabase.co/rest/v1/menu_cache?image_hash=eq.${imageHash}`,
              { method: "PATCH", headers: { "apikey": anonKey, "Authorization": `Bearer ${anonKey}`, "Content-Type": "application/json", "Prefer": "return=minimal" }, body: JSON.stringify({ hit_count: (cached.hit_count || 0) + 1 }) }
            ).catch(() => {});
          } else {
            console.log(`[menuCache] MISS for hash ${imageHash.substring(0, 12)}...`);
          }
        } catch (e) {
          console.error("[menuCache] lookup error:", fmtErr(e));
        }
      } else {
        console.warn("[menuCache] hash failed - skipping cache");
      }

      if (!data) {
        // Use streaming for faster response — items parsed progressively.
        //
        // Wave B.1: progressively push items into the items state as each
        // arrives from the streaming OCR. Cards appear in 0.8–1.5s instead
        // of waiting 3–5s for the full menu to parse. The post-resolve bulk
        // setItems below replaces these placeholder items with the fully
        // shaped, deduped, restaurant-keyed versions.
        const streamingItemsLocal: FoodItem[] = [];
        data = await analyzeMenuStreaming(base64, {
          onItem: (item, idx) => {
            if (idx === 0) console.log(`[stream] First item at ${Date.now()}ms: ${item.name}`);
            // Skip section headers and modifiers in the progressive render;
            // they need full context for proper rendering (restaurant name,
            // dedup against existing). They land in the post-resolve pass.
            if (item.sectionHeader || item.isModifier) return;
            const cleanName = sanitizeDisplayText(item.name) || item.name;
            const cleanOriginal = sanitizeDisplayText(item.originalName || item.original_name || item.name) || item.originalName || item.name;
            streamingItemsLocal.push({
              name: cleanName,
              description: item.description || "",
              price: item.price && item.price !== "null" ? item.price : "",
              dietaryTags: [],
              originalName: cleanOriginal,
              detectedLanguage: "en",
              allergens: [],
              currency: item.currency || "",
              imageKey: makeItemImageKey("Unknown Restaurant", item),
              isModifier: false,
              modifierGroupId: null,
              sectionHeader: false,
              menuType: "unknown",
              imageUrl: undefined,
              imageLoading: false,
            });
            // Render the progressive list. When appending pages, keep
            // already-accumulated items in front; on a fresh scan, show
            // only the streaming items.
            if (appendMode) {
              setItems([...pendingItemsRef.current, ...streamingItemsLocal]);
            } else {
              setItems([...streamingItemsLocal]);
            }
          },
        });
        // Store in cache for next time (background, non-blocking)
        if (imageHash && data && !data.error && data.items?.length > 0) {
          const anonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRuc3FtcHN4enZkeXh2dHVpZW50Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU4MTMyNzgsImV4cCI6MjA5MTM4OTI3OH0.xY5R4ow8QEHkQ4aAfRUt359iHemxM_PTa5fr5k-8VO0";
          fetch("https://tnsqmpsxzvdyxvtuient.supabase.co/rest/v1/menu_cache", {
            method: "POST",
            headers: { "apikey": anonKey, "Authorization": `Bearer ${anonKey}`, "Content-Type": "application/json", "Prefer": "return=minimal" },
            body: JSON.stringify({ image_hash: imageHash, restaurant_name: data.restaurantName || null, parsed_result: data }),
          }).then(r => {
            if (r.ok) console.log(`[menuCache] STORED for hash ${imageHash.substring(0, 12)}...`);
            else console.error(`[menuCache] insert failed: ${r.status}`);
          }).catch(() => {});
        }
      }

      if (data.error) {
        // Distinguish API failures from real "not a menu" so the toast doesn't lie.
        // analyzeMenuDirect tags API/network errors with "API_ERROR:" prefix.
        const isApiError = typeof data.error === "string" && data.error.startsWith("API_ERROR:");
        if (isApiError) {
          toast.error("Couldn't reach OCR right now — please try again in a moment", { duration: 5000 });
        } else if (data.isMenu === false) {
          toast.error("That doesn't look like a menu — try snapping a food menu!", { duration: 4000 });
        } else if (data.items.length === 0) {
          toast.error("Couldn't read that clearly — try holding the camera closer and straighter", { duration: 5000 });
        } else {
          toast.error(data.error, { duration: 4000 });
        }
        setPagesProcessing(prev => Math.max(0, prev - 1));
        if (pagesCompleted > 0) {
          // Stay in collecting view — previous pages still good
          return;
        }
        setSharingDishes([]);
        setSharingRestaurant("");
        setViewState("home");
        return;
      }

      const lang = data.detectedLanguage || "English";
      setDetectedLanguage(lang);
      setMenuType(data.menuType || "unknown");
      setAverageConfidence(typeof data.averageConfidence === "number" ? data.averageConfidence : null);

      // Handle non-menu images gracefully
      if (data.isMenu === false || data.menuType === "not-a-menu") {
        toast.error("That doesn't look like a menu — try snapping a food menu!", { duration: 4000 });
        setIsAnalyzing(false);
        setSharingDishes([]);
        setSharingRestaurant("");
        setViewState("home");
        return;
      }

      // Try to detect restaurant name from AI response
      const detectedName = cleanRestaurantName(data.restaurantName);
      if (detectedName) {
        setRestaurantName(detectedName);
        setRestaurantNameInput(detectedName);
      }
      setWaiterVisibleCount(6);

      const restaurantForImages = detectedName || "Unknown Restaurant";
      const foodItems: FoodItem[] = (data.items || []).map((item: any) => {
        const sectionHeader = !!item.sectionHeader;
        const itemImageKey = makeItemImageKey(restaurantForImages, item);
        const cleanedName = sanitizeDisplayText(item.name, lang) || item.name;
        const cleanedOriginalName = sanitizeDisplayText(item.originalName || item.original_name || item.name, item.detectedLanguage || lang) || item.originalName || item.original_name || item.name;
        return {
          name: cleanedName,
          description: item.description || "",
          price: item.price && item.price !== "null" ? item.price : "",
          dietaryTags: item.dietaryTags || item.dietary_tags || [],
          originalName: cleanedOriginalName,
          detectedLanguage: lang,
          allergens: item.allergens || [],
          currency: item.currency || "",
          nutrition: item.nutrition || undefined,
          imageKey: itemImageKey,
          confidence: typeof item.confidence === "number" ? item.confidence : undefined,
          isModifier: !!item.isModifier,
          modifierGroupId: item.modifierGroupId ?? null,
          sectionHeader,
          menuType: data.menuType || "unknown",
          imageUrl: undefined, // images fill in async via warmup
          imageLoading: false,
        };
      });

      // Filter out modifiers/add-ons that shouldn't appear as standalone dishes
      const isAddonPattern = (name: string) => {
        const lower = name.toLowerCase().trim();
        return /^(extra |add |side of |sub |upgrade |swap |make it |go |with )/.test(lower)
          || /^(lettuce wrap|gluten.?free bun|no bun)$/i.test(lower)
          || (lower.length < 15 && /^(extra \w+)$/i.test(lower));
      };
      const filteredItems = foodItems.filter(item => {
        if (item.sectionHeader) return true; // keep section headers
        if (item.isModifier) return false; // LLM-flagged modifier
        if (isAddonPattern(item.name)) return false; // pattern-based filter
        return true;
      });

      if (foodItems.length === 0 && !appendMode) {
        setPagesProcessing(prev => Math.max(0, prev - 1));
        tryIdentifyDish(base64);
        return;
      }

      // Detect restaurant name from first page
      const detectedNamePage = cleanRestaurantName(data.restaurantName);
      if (!restaurantName && detectedNamePage) {
        setRestaurantName(detectedNamePage);
        setRestaurantNameInput(detectedNamePage);
      }

      // Deduplicate against already-accumulated items
      const existingKeys = new Set(pendingItemsRef.current.map(it => stableDishKey(it)));
      const existingHeaders = new Set(
        pendingItemsRef.current.filter(it => it.sectionHeader).map(it => it.name.toLowerCase())
      );
      const dedupedNew = filteredItems.filter(it => {
        if (it.sectionHeader) return !existingHeaders.has(it.name.toLowerCase());
        return !existingKeys.has(stableDishKey(it));
      });
      const newFoodCount = dedupedNew.filter(it => !it.sectionHeader).length;

      // Accumulate
      pendingItemsRef.current = [...pendingItemsRef.current, ...dedupedNew];
      setItems([...pendingItemsRef.current]);
      setPageCount(prev => prev + 1);
      setPagesProcessing(prev => Math.max(0, prev - 1));
      setPagesCompleted(prev => prev + 1);

      if (newFoodCount > 0) {
        toast.success(`+${newFoodCount} dishes`, { duration: 1500 });
      } else if (appendMode) {
        toast.info("No new dishes on this page", { duration: 1500 });
      }

    } catch (_err) {
      // Error analyzing — try as dish photo
      tryIdentifyDish(base64);
    }
  };

  // Finish collecting pages and show results with image generation
  const handleFinishCollecting = () => {
    const allItems = items;
    const nonSectionItems = allItems.filter(f => !f.sectionHeader);
    const dishCount = nonSectionItems.length;

    if (dishCount === 0) {
      toast.error("No dishes found — try scanning again", { duration: 3000 });
      handleReset();
      return;
    }

    // Wave H1: celebratory success haptic when the user hits "let's eat!"
    // on a successful scan. iOS double-tap pop.
    successHaptic();

    // Save menu
    const autoMenuId = generateId("menu");
    setCurrentMenuId(autoMenuId);
    const finalName = restaurantNameInput.trim() || restaurantName || "Unknown Restaurant";
    saveMenu({
      id: autoMenuId,
      restaurantName: finalName,
      date: new Date().toISOString(),
      items: allItems,
      detectedLanguage: detectedLanguage,
      location: location || undefined,
      selectedDishIds: [],
      scanTimestamp: Date.now(),
      orderedDishIds: [],
      dishRatings: {},
    });

    // Schedule post-meal notification (1-1.5hr later)
    schedulePostMealNotification(finalName, autoMenuId, Date.now());

    // Store in Supabase (background)
    const parsePrice = (p?: string) => {
      if (!p) return null;
      const m = p.replace(/[^0-9.]/g, "");
      return m ? parseFloat(m) : null;
    };
    fetch("https://tnsqmpsxzvdyxvtuient.supabase.co/functions/v1/store-menu-scan", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRuc3FtcHN4enZkeXh2dHVpZW50Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU4MTMyNzgsImV4cCI6MjA5MTM4OTI3OH0.xY5R4ow8QEHkQ4aAfRUt359iHemxM_PTa5fr5k-8VO0",
      },
      body: JSON.stringify({
        restaurant_name: finalName,
        location: location ? { lat: location.lat, lng: location.lng, label: location.label } : null,
        detected_language: detectedLanguage,
        dishes: nonSectionItems.map(f => ({
          name: f.name,
          originalName: f.originalName || null,
          description: f.description || null,
          price: f.price || null,
          priceNumeric: parsePrice(f.price),
          category: null,
          calories: f.nutrition?.calories || null,
          dietaryTags: f.dietaryTags || null,
          allergens: f.allergens || null,
        })),
      }),
    }).catch(() => {});

    // Show results
    const totalImages = nonSectionItems.length;
    setTotalImagesToLoad(totalImages);
    setWaiterVisibleCount(6);

    const restaurantForImages = finalName;
    const menuFingerprint = computeMenuFingerprint(restaurantForImages, nonSectionItems);
    const fingerprintHit = checkMenuFingerprint(menuFingerprint);

    // Wave M4 — reveal-gating. Instead of dropping the user onto an empty
    // results grid that fills in over ~30-60s, hold a "plating your dishes"
    // loading screen (with rotating tips) until enough images are ready,
    // then reveal everything at once. Reveal fires on whichever comes
    // first: enough TOP-OF-PAGE images ready, or REVEAL_TIMEOUT elapsed.
    //
    // Wave P: target the ABOVE-THE-FOLD count (the 6 initially-visible
    // cards) rather than 20. Robin's QA: revealing on the 20s timeout with
    // only ~1 image ready is pointless. With the faster server gen, the
    // first 6 are ready quickly.
    //
    // Wave S: gate on the SPECIFIC top-of-page indexes loading, not any-6.
    // Previously, 6 random cache hits anywhere on the menu would satisfy
    // the gate while the top 4 cards stayed blank. We now track which
    // visible-set indexes have completed and reveal only when at least
    // VISIBLE_REVEAL_COUNT of them are populated. The 20s safety cap
    // remains so the user is never stuck on the loader.

    // Build the visible-set FIRST so the reveal gate can target it.
    const visibleCandidates = initialVisibleFoodTargets(allItems, 8);
    const initialVisibleIndexSet = new Set(visibleCandidates.map(({ originalIndex }) => originalIndex));
    const VISIBLE_REVEAL_COUNT = Math.min(6, initialVisibleIndexSet.size, totalImages);
    const REVEAL_TIMEOUT_MS = 20000;
    revealedRef.current = false;
    setPrepareReady(0);
    setPrepareTarget(VISIBLE_REVEAL_COUNT);
    setPrepareComplete(false);
    const completedVisibleSet = new Set<number>();
    const revealResults = () => {
      if (revealedRef.current) return;
      revealedRef.current = true;
      setViewState("results");
      showResultsRef.current = true;
      // Wave Q — let the global NotificationManager decide whether to show
      // the contextual notification priming sheet (first scan only).
      document.dispatchEvent(new Event("yumz:scan-success"));
    };
    // Wave T — flip the preparing screen into its "ready" state. The screen
    // itself doesn't change; only its content swaps from plating → CTA.
    const markPrepareComplete = () => setPrepareComplete(true);
    if (VISIBLE_REVEAL_COUNT <= 0) {
      // Nothing to wait for — go straight to results.
      revealResults();
    } else {
      setViewState("preparing");
      // Safety cap: after REVEAL_TIMEOUT_MS, show the "let's eat" CTA even
      // if fewer than VISIBLE_REVEAL_COUNT images came back, so the user is
      // never stuck on the loader.
      setTimeout(markPrepareComplete, REVEAL_TIMEOUT_MS);
    }
    toast.success(`${dishCount} dishes across ${pageCount} page${pageCount > 1 ? 's' : ''}!`);

    if (fingerprintHit) storeMenuFingerprint(menuFingerprint);

    // Seed loading state on visible items
    setItems(prev => prev.map((p, i) =>
      initialVisibleIndexSet.has(i) && !prev[i].imageUrl ? { ...p, imageLoading: true } : p
    ));

    let completedCount = 0;
    const onImageComplete = (idx: number, imageUrl: string | null) => {
      const finalUrl = toBlobUrl(imageUrl) || imageUrl;
      if (finalUrl) setImageMap(prev => ({ ...prev, [idx]: finalUrl }));
      setItems(prev =>
        prev.map((p, i) => {
          if (i !== idx || !p) return p;
          return { ...p, imageUrl: finalUrl || p.imageUrl || undefined, imageLoading: false };
        })
      );
      completedCount++;
      setImagesLoaded(completedCount);
      // Wave M4 — drive the "preparing" progress + trigger reveal once
      // enough images are ready (only count successful loads).
      // Wave S — only TOP-OF-PAGE indexes (initialVisibleIndexSet) count
      // toward the reveal gate, so the user never lands on a results page
      // with blanks at the top.
      if (finalUrl) {
        if (initialVisibleIndexSet.has(idx)) {
          completedVisibleSet.add(idx);
          setPrepareReady(completedVisibleSet.size);
          if (!revealedRef.current && completedVisibleSet.size >= VISIBLE_REVEAL_COUNT) {
            // Wave T — flip preparing→ready instead of auto-jumping to results.
            markPrepareComplete();
          }
        }
      }
      if (completedCount >= totalImages) {
        // Safety: if every image resolved (incl. misses) before the target
        // was hit, flip the CTA so the user can tap into results.
        markPrepareComplete();
        storeMenuFingerprint(menuFingerprint);
        setItems(currentItems => {
          updateMenu(autoMenuId, { items: [...currentItems] });
          import("@/lib/storage").then(m => m.refreshDishImages());
          return currentItems;
        });
      }
    };

    // Enrich items
    enrichMenuItems(allItems).then(enriched => {
      setItems(prev => prev.map((p, i) => {
        const e = enriched[i];
        if (!e || !p) return p;
        return {
          ...p,
          description: e.description || p.description,
          allergens: e.allergens?.length ? e.allergens : p.allergens,
          dietaryTags: e.dietaryTags?.length ? e.dietaryTags : p.dietaryTags,
          nutrition: e.nutrition || p.nutrition,
        };
      }));
    }).catch(() => {});

    // ── Wave M1 — server-side batch image gen (primary path) ──────────
    // One request handles the WHOLE menu: the gen-dish-images-batch edge
    // function runs exact-match → LLM intent-map → vision-verify →
    // generate at 20-way concurrency server-side (no iOS WKWebView
    // socket cap) and streams results back as each dish resolves.
    //
    // If the edge function is unavailable or errors before resolving
    // anything, we fall back to the legacy client-side path
    // (warmVisibleItems for the visible 8 + queueImageGeneration for the
    // rest) so the app degrades gracefully rather than showing no images.
    const allFoodTargets = allItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.sectionHeader && !item.imageUrl);

    // Mark ALL pending food items as loading (not just the first 8) —
    // server-side gen fills them in priority order, so every card should
    // read as "working" immediately.
    const allPendingIndexSet = new Set(allFoodTargets.map(({ index }) => index));
    setItems(prev => prev.map((p, i) =>
      allPendingIndexSet.has(i) && !prev[i].imageUrl ? { ...p, imageLoading: true } : p
    ));

    const handleServerProgress = async (idx: number, imageUrl: string | null) => {
      if (imageUrl) {
        const current = allItems[idx];
        if (current?.imageKey) {
          Promise.all(makeItemImageAliases(restaurantForImages, current).map(alias => cacheImageByItemKey(alias, imageUrl))).catch(() => {});
        }
      }
      onImageComplete(idx, imageUrl);
    };

    const runLegacyFallback = () => {
      void warmVisibleItems(
        visibleCandidates.map(({ item, originalIndex }) => ({
          dishName: item.name,
          originalName: item.originalName,
          description: item.description,
          restaurantName: restaurantForImages,
          itemKey: item.imageKey,
          index: originalIndex,
        })),
        async (idx, imageUrl) => { await handleServerProgress(idx, imageUrl); },
      ).then(({ flaggedSlugs }) => {
        if (flaggedSlugs.size > 0) {
          const newFlagged = new Set<number>();
          allItems.forEach((item, index) => {
            if (item.sectionHeader) return;
            const slug = canonicalDishName(item.name).replace(/\s+/g, "-");
            if (flaggedSlugs.has(slug)) newFlagged.add(index);
          });
          if (newFlagged.size > 0) {
            setFlaggedIndices(prev => {
              const merged = new Set(prev);
              newFlagged.forEach(i => merged.add(i));
              return merged;
            });
          }
        }
        allItems.forEach((item, index) => {
          if (item.sectionHeader || item.imageUrl || initialVisibleIndexSet.has(index)) return;
          const priority = looksLikeDrink(item) ? 1 : Math.max(1, 30 - index);
          queueImageGeneration(
            item.name, item.description, restaurantForImages, index,
            (idx, imageUrl) => {
              const finalUrl = toBlobUrl(imageUrl) || imageUrl;
              if (finalUrl) setImageMap(prev => ({ ...prev, [idx]: finalUrl }));
              setItems(prev => prev.map((p, i) => {
                if (i !== idx || !p) return p;
                return { ...p, imageUrl: finalUrl || p.imageUrl || undefined, imageLoading: false };
              }));
            },
            priority, item.imageKey,
          );
        });
      });
    };

    // Backstop: queue any items the server stream didn't resolve (e.g. it
    // crashed mid-stream on a huge all-fresh menu, or a few genuine gen
    // misses) via the legacy per-item queue. This guarantees every card
    // eventually gets an image even if the server path partially fails.
    const backstopUnresolved = (resolved: Set<number>) => {
      allFoodTargets.forEach(({ item, index }) => {
        if (resolved.has(index)) return;
        const priority = looksLikeDrink(item) ? 1 : Math.max(1, 30 - index);
        queueImageGeneration(
          item.name, item.description, restaurantForImages, index,
          (idx, imageUrl) => {
            const finalUrl = toBlobUrl(imageUrl) || imageUrl;
            if (finalUrl) setImageMap(prev => ({ ...prev, [idx]: finalUrl }));
            setItems(prev => prev.map((p, i) => {
              if (i !== idx || !p) return p;
              return { ...p, imageUrl: finalUrl || p.imageUrl || undefined, imageLoading: false };
            }));
          },
          priority, item.imageKey,
        );
      });
    };

    void warmVisibleItemsServerSide(
      allFoodTargets.map(({ item, index }) => ({
        dishName: item.name,
        originalName: item.originalName,
        description: item.description,
        restaurantName: restaurantForImages,
        itemKey: item.imageKey,
        index,
      })),
      async (idx, imageUrl) => { await handleServerProgress(idx, imageUrl); },
    ).then(({ ok, resolvedIndices }) => {
      if (!ok) {
        console.warn("[Index] server-side batch gen unavailable — falling back to client gen");
        runLegacyFallback();
      } else {
        // Server path worked; mop up any dishes it didn't resolve.
        backstopUnresolved(resolvedIndices);
      }
    }).catch(() => {
      runLegacyFallback();
    });

    setTimeout(() => {
      setItems(prev => prev.map(p => p?.imageLoading ? { ...p, imageLoading: false } : p));
    }, 60000);
  };

  const toggleSelect = (index: number) => {
    const item = items[index];
    if (!item || item.sectionHeader) return;

    // Toggle selection in results view
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleOrdered = (index: number) => {
    const item = items[index];
    if (!item || item.sectionHeader) return;
    const key = stableDishKey(item);
    setOrderedDishIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Save to menu in background
      if (currentMenuId) {
        updateMenu(currentMenuId, { orderedDishIds: [...next] });
      }
      return next;
    });
    toast.success(
      orderedDishIds.has(key) ? `Unmarked ${item.name}` : `Ordered: ${item.name} 🍽️`,
      { duration: 1200 }
    );
  };

  const [flaggedIndices, setFlaggedIndices] = useState<Set<number>>(new Set());

  const handleFlagImage = async (index: number) => {
    const item = items[index];
    if (!item) return;
    // Mark as flagged visually
    setFlaggedIndices(prev => new Set(prev).add(index));
    track("image_flagged", { dishName: item.name });

    // Submit flag via Edge Function
    try {
      const anonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRuc3FtcHN4enZkeXh2dHVpZW50Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU4MTMyNzgsImV4cCI6MjA5MTM4OTI3OH0.xY5R4ow8QEHkQ4aAfRUt359iHemxM_PTa5fr5k-8VO0";
      await fetch("https://tnsqmpsxzvdyxvtuient.supabase.co/functions/v1/store-dish-image", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
        body: JSON.stringify({
          dish_name: item.name,
          restaurant_name: restaurantName || null,
          description: item.description || null,
          image_data: item.imageUrl || "",
          source: "flagged",
        }),
      });
    } catch { /* non-critical */ }
  };

  const handleSave = () => {
    setIsSaving(true);
    const finalName = restaurantNameInput.trim() || restaurantName || "Unknown Restaurant";
    const menuId = currentMenuId || generateId("menu");

    // Update existing auto-saved menu or create new
    if (currentMenuId) {
      updateMenu(currentMenuId, {
        restaurantName: finalName,
        items,
        detectedLanguage,
        location: location || undefined,
        selectedDishIds: [],
      });
    } else {
      saveMenu({
        id: menuId,
        restaurantName: finalName,
        date: new Date().toISOString(),
        items,
        detectedLanguage,
        location: location || undefined,
        selectedDishIds: [],
      });
    }

    // Save selected dishes
    const selectedDishEntries: SavedDish[] = Array.from(selectedIndices).map(
      (idx) => {
        const item = items[idx];
        return {
          id: generateId("dish"),
          menuId,
          name: item.name,
          description: item.description,
          price: item.price,
          imageUrl: item.imageUrl,
          dietaryTags: item.dietaryTags,
          originalName: item.originalName,
          detectedLanguage: item.detectedLanguage,
          allergens: item.allergens,
          currency: item.currency,
          nutrition: item.nutrition,
          restaurantName: finalName,
          orderedAt: new Date().toISOString(),
          location: location || undefined,
        };
      }
    );

    if (selectedDishEntries.length > 0) {
      saveDishes(selectedDishEntries);

      // Schedule rating reminders
      addPendingRatings(
        selectedDishEntries.map((d) => ({ dishId: d.id, dishName: d.name }))
      );
    }

    toast.success(
      `Saved ${selectedDishEntries.length} dish${selectedDishEntries.length !== 1 ? "es" : ""} from ${finalName}!`
    );

    // Go to sharing view instead of home
    setSharingDishes(
      selectedDishEntries.map((d) => ({ name: d.name, imageUrl: d.imageUrl, price: d.price, description: d.description }))
    );
    setSharingRestaurant(finalName);
    setSharingLocation(location?.label);
    setGroupPhoto(null);
    setIsSaving(false);
    setViewState("sharing");
  };

  const handleGroupCameraFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setGroupPhoto(reader.result as string);
      // Auto-switch to Photo Card when a photo is added
      setShareMode("photo-card");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleShareToSocial = async (platform: "instagram" | "snapchat" | "tiktok" | "generic") => {
    if (!sharingDishes.length && !sharePreviewUrl) {
      toast.error("Nothing to share yet");
      return;
    }
    setSharingPlatform(platform);

    try {
      // Use the already-generated preview if available (avoids double generation)
      let blob: Blob | null = null;
      if (sharePreviewUrl) {
        const resp = await fetch(sharePreviewUrl);
        blob = await resp.blob();
      }

      if (!blob) {
        // Generate fresh if no preview
        const context = {
          restaurantName: sharingRestaurant,
          items: sharingDishes,
          shareMode,
          tone: shareTone,
          groupPhotoUrl: groupPhoto,
          locationLabel: sharingLocation,
        };
        const { prepareSharePayload, sharePreparedPayload } = await import("@/lib/shareTargets");
        const payload = await Promise.race([
          prepareSharePayload(
            platform === "snapchat" ? "snapchat" : "system",
            context,
          ),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Card generation timed out")), 15000)),
        ]);
        setSharingPlatform(null);
        const success = await sharePreparedPayload(payload);
        if (success) toast.success("shared!");
        return;
      }

      // Share the preview blob directly via Capacitor
      const file = new File([blob], `yumz-share-${Date.now()}.jpg`, { type: "image/jpeg" });
      const funLine = SHARE_TONES.find(t => t.id === shareTone)?.shareText(sharingRestaurant || "this place") || "";
      const shareText = `${funLine}\n\nget yours free: yumz.social`;

      setSharingPlatform(null);

      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const { Filesystem, Directory } = await import("@capacitor/filesystem").then(m => ({ Filesystem: m.Filesystem, Directory: m.Directory }));
          const { Share } = await import("@capacitor/share");
          const arrayBuffer = await blob.arrayBuffer();
          const base64 = btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ""));
          const fileName = `yumz-share-${Date.now()}.jpg`;
          const result = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
          // Pass image as url (not files) — better compatibility with Snapchat
          await Share.share({
            title: `yumz @ ${sharingRestaurant}`,
            url: result.uri,
            dialogTitle: "Share your yumz",
          });
          toast.success("shared!");
          return;
        }
      } catch { /* fall through to web share */ }

      // Web share fallback
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: `yumz @ ${sharingRestaurant}`, text: shareText, files: [file] });
        toast.success("shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("downloaded!");
      }
    } catch (e) {
      console.error("Share failed:", fmtErr(e));
      toast.error(`Share failed: ${e instanceof Error ? e.message : "try again"}`);
      setSharingPlatform(null);
    }
  };

  const handleSkipSharing = () => {
    // Go back to results, not home — user still wants to see their dishes
    setSharingDishes([]);
    setSharingRestaurant("");
    setSharingLocation(undefined);
    setGroupPhoto(null);
    setSharePreviewUrl(null);
    setViewState("results");

    // Check for rating prompt
    const unrated = getUnratedDishesAfterOneHour();
    if (unrated.length > 0) {
      setRatingDishes(unrated);
      setShowRating(true);
    }
  };

  const handleRating = (dishId: string, rating: "loved" | "not-for-me") => {
    updateDish(dishId, { rating, ratedAt: new Date().toISOString() });
    markPendingAsPrompted([dishId]);
  };

  const handleCloseRating = () => {
    setShowRating(false);
    markPendingAsPrompted(ratingDishes.map((d) => d.dishId));
  };

  const handleReset = () => {
    setItems([]); setImageMap({}); setFlaggedIndices(new Set());
    setSelectedIndices(new Set());
    imageSetRef.current.clear();
    setViewState("home");
    setShowHome(false);
    setRestaurantName("");
    setRestaurantNameInput("");
    setLocation(null);
    setRestaurantConfirmed(false);
    setEditingRestaurant(false);
    setSharingDishes([]);
    setSharingRestaurant("");
    setSharingLocation(undefined);
    setGroupPhoto(null);
    setSharePreviewUrl(null);
    setCurrentMenuId(null);
    setPageCount(0);
    setPagesProcessing(0);
    setPagesCompleted(0);
    pendingItemsRef.current = [];
    isAppendModeRef.current = false;
    setOrderedDishIds(new Set());
  };

  // Allergen helpers
  const hasAllergenConfig = settings.allergens.length > 0;
  const hasAllergenMatch = (item: FoodItem) => {
    if (!settings.allergens.length || !item.allergens?.length) return false;
    const userLower = settings.allergens.map((a) => a.toLowerCase());
    return item.allergens.some((a) => userLower.includes(a.toLowerCase()));
  };

  const safeCount = items.filter((it) => !hasAllergenMatch(it)).length;
  const warningCount = items.filter((it) => hasAllergenMatch(it)).length;

  const filteredItems =
    !hasAllergenConfig || allergyFilter === "all"
      ? items
      : allergyFilter === "safe"
      ? items.filter((it) => it.sectionHeader || !hasAllergenMatch(it))
      : items.filter((it) => it.sectionHeader || hasAllergenMatch(it));

  const isDrinkLike = isDrinkItem;
  const isDessertLike = isDessertItem;

  const rankedSections = rankMenuItems(items, sortMode, hasAllergenMatch, allergyFilter);
  const visibleMainItems = rankedSections.main;
  const visibleDrinkItems = rankedSections.drinks;
  const visibleWaiterItems = visibleMainItems;

  // Analyzing View with progress steps
  const AnalyzingView = ({ onCancel }: { onCancel: () => void }) => {
    const [step, setStep] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [factIndex, setFactIndex] = useState(() => Math.floor(Math.random() * 50));
    const [factVisible, setFactVisible] = useState(true);

    // Rotating animated emojis that make people smile
    const foodFacts: [string, string][] = [
      ["🍕", "The world's most expensive pizza costs $12,000 and takes 72 hours to make"],
      ["🥑", "Avocados are technically berries. Strawberries aren't."],
      ["🍝", "Italy has over 600 pasta shapes. Most restaurants use about 10."],
      ["🌶️", "Capsaicin tricks your brain into thinking you're literally on fire"],
      ["🍣", "Sushi rice was originally used to ferment fish, not eat"],
      ["👨‍🍳", "French chefs learn 5 'mother sauces' — every other sauce comes from these"],
      ["🥐", "Croissants were invented in Austria, not France"],
      ["🍫", "White chocolate isn't technically chocolate — zero cocoa solids"],
      ["🥩", "Wagyu cattle in Japan get daily massages and listen to classical music"],
      ["🍋", "Lemons float but limes sink. It's a density thing."],
      ["🌮", "Tacos al pastor was inspired by Lebanese shawarma brought to Mexico"],
      ["🌺", "Vanilla is the world's most popular flavor but the second most expensive spice"],
      ["🧅", "Onions make you cry because they release a gas that forms acid in your tears"],
      ["🍔", "The average American eats 50 billion burgers a year. 3 per person per week."],
      ["🥟", "Dumplings exist in almost every cuisine — pierogi, gyoza, empanadas, momo"],
      ["🍰", "Tiramisu literally means 'pick me up' in Italian"],
      ["🫒", "Olives straight from the tree taste absolutely terrible. Months of curing needed."],
      ["🍜", "Ramen was invented in China but perfected in Japan"],
      ["🧀", "A wheel of Parmigiano-Reggiano takes 2 years and 131 gallons of milk"],
      ["🌽", "Corn is technically a grass. Your salad is basically lawn clippings."],
      ["🥖", "French law says bread can only be flour, water, salt, and yeast"],
      ["🐔", "Chickens outnumber humans 3 to 1 on Earth"],
      ["🍾", "Champagne has about 49 million bubbles per bottle"],
      ["🥭", "Mangoes are related to cashews and poison ivy. Wild."],
      ["🍩", "Donuts have holes because a sea captain thought the center was always raw"],
      ["☕", "Coffee beans aren't beans — they're pits of a cherry-like fruit"],
      ["🐙", "Octopus can solve mazes. You're eating a genius."],
      ["🌿", "Basil means 'king' in Greek. Royalty for your pasta."],
      ["🥚", "Egg shell color depends on the chicken's earlobes. Seriously."],
      ["🍿", "Popcorn can pop up to 3 feet in the air"],
      ["🥕", "Carrots were originally purple. Orange ones were bred in the Netherlands."],
      ["🍪", "The chocolate chip cookie was invented by accident in 1938"],
      ["🍯", "Honey never spoils. 3000-year-old honey from Egyptian tombs — still edible."],
      ["🍌", "Bananas are radioactive. You'd need 10 million at once for it to matter."],
      ["🥥", "Coconut water can be used as emergency IV fluid"],
      ["🫑", "Bell peppers change color as they ripen — green → yellow → orange → red"],
      ["👨‍🍳", "A chef's hat traditionally has 100 pleats — one for each way to cook an egg"],
      ["🦞", "Lobster used to be prison food. The cockroach of the sea."],
      ["🥜", "Peanuts aren't nuts — they're legumes that grow underground"],
      ["🥗", "Caesar salad was invented in Mexico by an Italian immigrant, not in Rome"],
      ["🐝", "It takes 12 bees their entire lifetime to make one teaspoon of honey"],
      ["🫕", "Fondue is Switzerland's national dish — promoted to boost cheese sales"],
      ["🍵", "Tea is the most consumed beverage in the world after water"],
      ["🧆", "McDonald's sells McFalafel in the Middle East"],
      ["🍅", "Ketchup was sold as medicine in the 1830s"],
      ["🍍", "Pineapples take 2-3 years to grow. Each plant makes just one."],
      ["🫚", "Nutmeg is a hallucinogen in large doses. Don't try it."],
      ["🧀", "The most stolen food in the world is cheese"],
      ["🍦", "Astronaut ice cream was never actually eaten in space"],
      ["🥫", "Spam is short for 'spiced ham' and is huge in Hawaii and South Korea"],
      ["🫘", "Coffee was discovered by Ethiopian goats who got hyper eating the berries"],
      ["🦑", "Squid ink pasta was originally a fisherman's meal — waste nothing"],
      ["🍇", "It takes about 2.5 pounds of grapes to make one bottle of wine"],
      ["🧈", "Butter was used as currency in medieval Norway"],
      ["🥒", "Cucumbers are 96% water. You're basically eating a crunchy drink."],
      ["🐄", "A cow gives nearly 200,000 glasses of milk in her lifetime"],
      ["🍑", "Peaches and almonds are in the same family. Cousins."],
      ["🫛", "Wasabi is so hard to grow that most restaurants serve dyed horseradish"],
      ["🦐", "Shrimp hearts are in their heads. Romantic."],
      ["🍊", "The color orange was named after the fruit, not the other way around"],
      ["🧊", "Ice cream headaches happen because cold hits a nerve cluster on the roof of your mouth"],
      ["🥬", "Lettuce is a member of the sunflower family"],
      ["🫧", "Sparkling water was invented by Joseph Priestley — who also discovered oxygen"],
      ["🐟", "Swordfish and marlin are the fastest fish — 68 mph. Your sushi was an athlete."],
      ["🍐", "Pears ripen from the inside out. By the time the outside is soft, the core is mush."],
      ["🥞", "Pancakes are one of the oldest foods — ancient Greeks ate them with honey"],
      ["🫐", "Blueberries are one of the only naturally blue foods in nature"],
      ["🌰", "Cashews grow on the bottom of a fruit called a cashew apple"],
      ["🍗", "KFC is a Christmas tradition in Japan since 1974"],
      ["🧄", "Garlic can be used as an adhesive. Vampires AND glue."],
      ["🥝", "Kiwis have more vitamin C than oranges, gram for gram"],
      ["🐖", "Pigs have 15,000 taste buds. Humans have about 10,000."],
      ["🫔", "Tamales date back to 8000 BC — older than the wheel"],
      ["🍄", "Mushrooms are genetically closer to humans than to plants"],
      ["🥄", "The fork wasn't common in Europe until the 18th century. Fingers first."],
    ];

    const stepLabels = ["Scanning menu", "Parsing dishes & calories", "Detecting location", "Checking for translations", "Estimating calories", "Images will load on next screen"];

    useEffect(() => {
      // Cycle through steps continuously
      const stepInterval = setInterval(() => {
        setStep(prev => (prev + 1) % stepLabels.length);
      }, 2500);
      const ticker = setInterval(() => setElapsed(e => e + 1), 1000);
      // Rotate fun facts every 5 seconds with fade
      const factTimer = setInterval(() => {
        setFactVisible(false);
        setTimeout(() => {
          setFactIndex(prev => (prev + 1) % foodFacts.length);
          setFactVisible(true);
        }, 400);
      }, 7000);
      return () => { clearInterval(stepInterval); clearInterval(ticker); clearInterval(factTimer); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dishCount = items.filter(i => !i.sectionHeader).length;

    return (
      <div className="flex flex-col items-center justify-center px-6 page-fade-in" style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>

        {/* Big animated emoji — the star of the show */}
        <div className="relative mb-6">
          <div className="absolute inset-[-16px] rounded-full border border-primary/10 analyzing-ring" />
          <div className="absolute inset-[-8px] rounded-full border border-primary/15 analyzing-ring" style={{ animationDelay: "0.5s" }} />
          <div
            className={`text-7xl transition-all duration-500 ${factVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}
            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))' }}
          >
            {foodFacts[factIndex % foodFacts.length][0]}
          </div>
        </div>

        {/* "did you know" label */}
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 mb-2">did you know?</p>

        {/* The fact — BIG and prominent */}
        <div className="w-full max-w-xs min-h-[80px] flex items-center justify-center mb-8">
          <p
            className={`text-lg font-semibold text-center text-foreground leading-snug px-2 transition-all duration-500 ${
              factVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            {foodFacts[factIndex % foodFacts.length][1]}
          </p>
        </div>

        {/* Dish count badge */}
        {dishCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 mb-4">
            <ForkKnife weight="bold" className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-bold text-primary">
              {dishCount} dishes found{dishCount > 60 ? " · big menu!" : dishCount > 30 ? " · solid menu" : ""}
            </span>
          </div>
        )}

        {/* Compact progress — small dots + current step label */}
        <div className="flex items-center gap-3 mb-4">
          {stepLabels.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-500 ${
                i < step ? "bg-green-400 w-2" : i === step ? "bg-primary w-6" : "bg-gray-200 w-2"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground mb-1">
          {stepLabels[step] || "Working..."}...
        </p>

        {elapsed > 20 && dishCount > 40 && (
          <p className="text-xs text-muted-foreground mt-2 animate-pulse">{dishCount} dishes takes a sec — hang tight!</p>
        )}

        <button
          onClick={onCancel}
          className="mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors px-4 py-2 rounded-2xl hover:bg-muted"
        >
          <X className="w-4 h-4" />
          cancel
        </button>

        {/* AI disclaimer */}
        <p className="mt-8 text-[11px] text-muted-foreground/50 text-center max-w-[260px] leading-relaxed">
          Images are AI-generated and sometimes incorrect. Use the flag on any dish to send us feedback — we'll correct it for next time.
        </p>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${viewState === "home" && !showHome ? "bg-[#0F0F0F]" : "bg-[#FAF8F5]"}`}>
        {indexError && (
        <div className="fixed inset-0 z-[9999] bg-red-50 border-4 border-red-400 overflow-auto">
          <div className="p-4 text-red-900">
            <h2 className="text-xl font-bold mb-2">YUMZ CRASH REPORT</h2>
            <p className="text-sm font-mono mb-4 break-all">{indexError}</p>
            <button onClick={() => setIndexError(null)} className="px-4 py-2 bg-red-600 text-white rounded-xl">Dismiss</button>
          </div>
        </div>
      )}
      {/* Hidden camera inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleCameraFile}
      />
      <input
        ref={groupCameraRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleGroupCameraFile}
      />

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* ─── CAMERA-FIRST VIEW (default landing) ─────────────────────── */}
      {viewState === "home" && !showHome && (
        <div className="flex flex-col page-fade-in" style={{ position: 'fixed', inset: 0, background: '#0F0F0F', overflow: 'hidden' }}>
          {/* Top bar — floating over camera */}
          <div className="safe-area-top flex items-center justify-between px-5 pt-2 pb-1 relative z-20">
            <h1 className="text-xl font-extrabold text-white tracking-tight drop-shadow-lg">yumz</h1>
            <button
              onClick={() => navigate("/notifications")}
              className="relative text-white/80 hover:text-white transition-colors p-2 rounded-full bg-black/30 backdrop-blur-md"
            >
              <Bell size={20} weight="bold" />
              {notifUnread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-[9px] text-white font-bold flex items-center justify-center">{notifUnread > 9 ? '9+' : notifUnread}</span>
              )}
            </button>
          </div>

          {/* Live camera viewfinder — FULL SCREEN */}
          <div className="flex-1 relative">
            {/* Live video feed — edge to edge */}
            {!cameraError && (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                  cameraReady ? "opacity-100" : "opacity-0"
                }`}
              />
            )}

            {/* Loading state while camera initializes */}
            {!cameraReady && !cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-3 animate-pulse">
                  <Camera className="w-8 h-8 text-white/40" />
                </div>
                <p className="text-white/30 text-xs">starting camera...</p>
              </div>
            )}

            {/* Fallback when camera access denied */}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/[0.06] flex items-center justify-center mb-4">
                  <Camera className="w-10 h-10 text-white/50" />
                </div>
                <h2 className="text-white text-lg font-bold mb-1">snap the menu</h2>
                <p className="text-white/40 text-xs text-center px-8">
                  tap the button below to open your camera
                </p>
              </div>
            )}

          </div>




          {/* Identifying dish spinner */}
          {identifyingDish && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4 animate-pulse">
                <ForkKnife weight="bold" className="w-8 h-8 text-white/80" />
              </div>
              <h2 className="text-white text-xl font-extrabold mb-2">hmm, not a menu...</h2>
              <p className="text-white/50 text-sm">checking if it's a dish...</p>
            </div>
          )}

          {/* Dish match result */}
          {dishMatch && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm px-8">
              <Fire weight="fill" className="w-12 h-12 text-orange-400 mb-4" />
              <h2 className="text-white text-2xl font-extrabold mb-1 text-center">
                {dishMatch.dishName}
              </h2>
              {dishMatch.matchedRestaurant && (
                <p className="text-primary text-sm font-semibold mb-1">
                  @ {dishMatch.matchedRestaurant}
                </p>
              )}
              <p className="text-white/50 text-xs mb-6 text-center">
                {dishMatch.matchedRestaurant
                  ? "found it in your recent menus!"
                  : "looks delicious — save it?"}
              </p>
              <div className="flex gap-3 w-full max-w-xs">
                <button
                  onClick={() => {
                    handleSaveDishMatch();
                  }}
                  className="flex-1 py-3 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-sm active:scale-95 transition-transform"
                >
                  add to faves 🫶
                </button>
                <button
                  onClick={() => setDishMatch(null)}
                  className="px-5 py-3 rounded-2xl bg-white/10 text-white/70 font-medium text-sm active:scale-95 transition-transform"
                >
                  nah
                </button>
              </div>
            </div>
          )}

          {/* No menu AND not food */}
          {noMenuDetected && !identifyingDish && !dishMatch && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
              <Warning weight="fill" className="w-16 h-16 text-white/50 mb-4" />
              <h2 className="text-white text-2xl font-extrabold mb-2">no dishes found, try again</h2>
              <p className="text-white/40 text-sm mb-8">that doesn't look like a menu</p>
              <button
                onClick={() => {
                  setNoMenuDetected(false);
                  if (cameraReady && !cameraError) {} else { triggerCamera(); }
                }}
                className="w-16 h-16 rounded-full border-[3px] border-white/50 flex items-center justify-center active:scale-90 transition-transform"
              >
                <div className="w-12 h-12 rounded-full bg-white" />
              </button>
              <span className="text-white/50 text-xs mt-3">tap to try again</span>
            </div>
          )}
        </div>
      )}


      {/* ─── HOME VIEW (my spots browse mode) ─────────────────────────── */}
      {viewState === "home" && showHome && (
        <div className="min-h-screen flex flex-col page-fade-in">
          {/* Header */}
          <header className="safe-area-top px-6 pt-2 pb-1 flex items-center justify-between">
            <button
              onClick={handleReset}
              className="flex items-center gap-2.5"
            >
              <div className="w-10 h-10 rounded-3xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md shadow-primary/20">
                <ForkKnife className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-extrabold text-foreground tracking-tight block leading-tight">
                  yumz
                </span>
                <span className="text-[10px] text-muted-foreground font-medium tracking-widest uppercase">
                  snap it. eat it. share it.
                </span>
              </div>
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate("/notifications")}
                className="p-2.5 hover:bg-muted rounded-2xl transition-colors relative"
                title="Notifications"
              >
                <Bell className="w-5 h-5 text-muted-foreground" />
                {notifUnread > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-[9px] text-white font-bold flex items-center justify-center">{notifUnread > 9 ? '9+' : notifUnread}</span>
                )}
              </button>
              {/* Wave L2 — labelled allergies chip. Icon-alone shield-with-!
                  was unclear; the inline label makes the purpose
                  obvious without breaking header layout. */}
              <button
                onClick={() => setSettingsOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-2 hover:bg-muted rounded-2xl transition-colors"
                title="Allergies"
              >
                <ShieldWarning className="w-4 h-4 text-muted-foreground" weight="fill" />
                <span className="text-xs font-bold text-muted-foreground">allergies</span>
              </button>
            </div>
          </header>

          {/* Hero area */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 -mt-8">
            {/* Scan button */}
            <button
              onClick={triggerCamera}
              className="scan-button w-36 h-36 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-2xl shadow-primary/30 hover:shadow-primary/50 active:scale-95 transition-all duration-200 mb-8"
            >
              <Camera className="w-14 h-14 text-white" />
            </button>

            <h2 className="text-3xl font-extrabold text-foreground mb-2 text-center">
              snap a menu
            </h2>
            <p className="text-base text-muted-foreground text-center max-w-xs">
              snap it. eat it. share it.
            </p>
          </div>

          {/* Empty state — shown when no dishes selected yet */}
          <div className="px-6 pb-40">
            <div className="bg-gradient-to-br from-orange-50/80 to-amber-50/80 rounded-3xl p-5 text-center">
              <Camera size={28} weight="light" className="text-orange-400 mb-2 mx-auto" />
              <p className="text-sm font-medium text-foreground mb-1">snap it. eat it. share it.</p>
              <p className="text-xs text-muted-foreground">
                tap the snap button to scan a menu.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─── ANALYZING VIEW (dead state — kept for backward-compat) ──
          setViewState("analyzing") is no longer dispatched anywhere; the
          flow now goes camera → "collecting" directly and the loading hero
          lives inside CollectingView (DidYouKnowFact rotator). This branch
          can be removed in a follow-up commit once we're sure no off-path
          code path dispatches "analyzing". */}
      {viewState === "analyzing" && (
        <AnalyzingView onCancel={handleReset} />
      )}

      {/* ─── LOADING FLOW (Wave V — 2 screens, not 3) ──────────────────────
          Robin's spec: collapse to TWO interactive screens.

          Screen A — "any other pages?" (viewState === "collecting"):
            OCR runs silently in the background. User chooses:
              • add another page  → loops captureFrame in append mode
              • no, plate them up → handleFinishCollecting (disabled while
                OCR is still processing or no dishes detected yet)

          Screen B — "chef is plating your dishes" (viewState === "preparing"):
            Image-gen runs. "Let's Eat" button starts greyed and turns
            orange (primary) when prepareComplete flips true. "Start over"
            is always available.

          Both screens share the same gradient + container so the swap
          feels like a single page morphing, not three pages. */}
      {(viewState === "collecting" || viewState === "preparing") && (() => {
        const dishCount = items.filter(it => !it.sectionHeader).length;
        const isProcessing = pagesProcessing > 0;
        const inCollecting = viewState === "collecting";
        const inPreparing = viewState === "preparing";
        const readyToReveal = inPreparing && prepareComplete;
        return (
          <div className="min-h-screen flex flex-col items-center justify-center px-6 page-fade-in" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 50%, #f0fdf4 100%)' }}>
            <div className="w-full max-w-sm text-center space-y-6">

              {inCollecting ? (
                // ─── Screen A — "any other pages?" ──────────────────────
                <>
                  <div className="space-y-3">
                    <div className="text-5xl mb-1">📄</div>
                    <h2 className="text-2xl font-extrabold text-foreground">any other pages?</h2>
                    <p className="text-sm text-muted-foreground">
                      {isProcessing
                        ? (pagesCompleted > 0 ? `still reading page ${pagesCompleted + 1}…` : "reading the menu in the background…")
                        : dishCount > 0
                          ? <><span className="font-bold text-foreground">{dishCount}</span> dishes ready so far{restaurantName ? <> at <span className="font-semibold text-foreground">{restaurantName}</span></> : ''}</>
                          : "tap a page to add it"}
                    </p>
                    {isProcessing && (
                      <div className="flex justify-center gap-1 mt-2">
                        {[0, 1, 2].map(i => (
                          <div key={i} className="w-2 h-2 rounded-full bg-primary" style={{ animation: `bounce 1.4s infinite ease-in-out ${i * 0.16}s`, opacity: 0.7 }} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        isAppendModeRef.current = true;
                        captureFrame();
                      }}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-gray-300 text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-muted-foreground disabled:hover:bg-transparent"
                    >
                      <Camera weight="bold" className="w-4 h-4" />
                      add another page
                    </button>
                    <button
                      onClick={handleFinishCollecting}
                      disabled={isProcessing || dishCount === 0}
                      className="w-full py-4 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white font-extrabold text-base shadow-lg active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:bg-gradient-to-br disabled:from-gray-300 disabled:to-gray-300"
                    >
                      {isProcessing ? "still reading…" : "no, plate them up 🍳"}
                    </button>
                  </div>

                  <button
                    onClick={handleReset}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    start over
                  </button>
                </>
              ) : (
                // ─── Screen B — "chef is plating your dishes" ───────────
                <>
                  <DidYouKnowFact paused={readyToReveal} rotateMs={4500} />

                  <div className="space-y-2">
                    <h2 className="text-2xl font-extrabold text-foreground">
                      found <span className="text-primary">{dishCount}</span> {dishCount === 1 ? "dish" : "dishes"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {readyToReveal
                        ? "your menu is ready"
                        : "chef is plating your dishes — taking photos of every plate"}
                    </p>
                    {!readyToReveal && (
                      <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden mt-3">
                        <div
                          className="h-full bg-primary transition-all duration-500 ease-out"
                          style={{ width: `${prepareTarget > 0 ? Math.min(100, Math.round((prepareReady / prepareTarget) * 100)) : 100}%` }}
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      if (!readyToReveal || revealedRef.current) return;
                      revealedRef.current = true;
                      showResultsRef.current = true;
                      setViewState("results");
                      document.dispatchEvent(new Event("yumz:scan-success"));
                    }}
                    disabled={!readyToReveal}
                    className={
                      readyToReveal
                        ? "w-full py-4 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white font-extrabold text-lg shadow-lg active:scale-[0.98] transition-transform"
                        : "w-full py-4 rounded-2xl bg-gray-200 text-gray-400 font-extrabold text-lg cursor-not-allowed"
                    }
                  >
                    let's eat 🍴
                  </button>

                  <button
                    onClick={handleReset}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    start over
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ─── RESULTS VIEW ─────────────────────────────────────────────── */}
      {viewState === "results" && (
        <div className="min-h-screen flex flex-col page-fade-in">
          {/* Results header */}
          <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-gray-100 safe-area-top">

            <div className="px-4">
              <div className="flex items-center justify-between h-11">
                <button
                  onClick={handleReset}
                  className="p-2 -ml-2 hover:bg-muted rounded-2xl transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
                <h1 className="text-lg font-extrabold text-foreground">
                  my order
                </h1>
                {/* Wave L2 — labelled allergies chip; matches the
                    home-screen header treatment for consistency. */}
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="flex items-center gap-1.5 px-2 py-2 -mr-1 hover:bg-muted rounded-2xl transition-colors"
                  title="Allergies"
                >
                  <ShieldWarning className="w-4 h-4 text-muted-foreground" weight="fill" />
                  <span className="text-xs font-bold text-muted-foreground">allergies</span>
                </button>
              </div>
            </div>
          </header>

          {/* Restaurant name */}
          <div className="px-4 py-3 bg-gray-50/50">
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                value={restaurantNameInput}
                onChange={(e) => setRestaurantNameInput(e.target.value)}
                placeholder="type restaurant name here"
                className={`w-full border-2 rounded-2xl px-4 py-3 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors ${
                  restaurantNameInput
                    ? "bg-green-50/50 border-green-300 text-foreground"
                    : "bg-red-50 border-red-200 text-foreground placeholder:text-red-400"
                }`}
              />
              {restaurantName && restaurantNameInput === restaurantName && (
                <span className="detected-badge absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                  <Check weight="bold" className="w-3 h-3" /> Detected
                </span>
              )}
            </div>
            {/* Page count badge */}
            {pageCount > 1 && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                  {pageCount} pages scanned
                </span>
              </div>
            )}
          </div>

          {/* Allergy filter */}
          {hasAllergenConfig && (
            <div className="px-4 pt-3">
              <AllergyFilterBar
                filter={allergyFilter}
                onChange={setAllergyFilter}
                totalCount={items.length}
                safeCount={safeCount}
                warningCount={warningCount}
              />
            </div>
          )}

          {/* Dishes grid */}
          <div className="flex-1 px-4 py-4 pb-52">
            {visibleWaiterItems.length === 0 ? (
              <div className="h-[50vh] flex flex-col items-center justify-center text-center px-6">
                <div className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin mb-4" />
                <h3 className="text-base font-semibold text-foreground">Loading your picks…</h3>
                <p className="text-sm text-muted-foreground mt-1">Menu parsed — just binding the dishes now.</p>
              </div>
            ) : (
              <>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-3 pb-1">
              {[
                ["menu", "menu order"],
                ["smart", "for you"],
                ["protein", "gym mode"],
                ["low-cal", "cut mode"],
                ["light", "main character"],
                ["tiny", "just a lil one"],
                ["low-sugar", "sweet skip"],
              ].map(([id,label]) => (
                <button key={id} onClick={() => setSortMode(id as SortMode)} className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${sortMode === id ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-gray-200"}`}>
                  {label}
                </button>
              ))}
            </div>
            {selectedIndices.size > 0 && (
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3">
                {selectedIndices.size === 1 ? "1 picked" : `${selectedIndices.size} picked`}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">

              {visibleWaiterItems.map(({ item, originalIndex }, fi) => {
                const resolvedImageUrl = imageMap[originalIndex] || item.imageUrl;
                const imageReady = !!resolvedImageUrl;
                if (fi === 0) console.log(`[RENDER] fi=0, idx=${originalIndex}, imageMapKeys=${Object.keys(imageMap).join(',')}, imageMapVal=${imageMap[originalIndex]?.slice(0,30)}, itemUrl=${item.imageUrl?.slice(0,30)}, ready=${imageReady}`);
                const isSelected = selectedIndices.has(originalIndex);
                const allergenDanger =
                  hasAllergenConfig && hasAllergenMatch(item);

                if (item.sectionHeader) {
                  return (
                    <div
                      key={`section-${item.name}-${originalIndex}`}
                      className="col-span-2 flex items-center gap-3 pt-5 pb-2"
                    >
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                      <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border border-primary/10 rounded-full">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary/70">
                          {item.name}
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                    </div>
                  );
                }

                return (
                  <button
                    key={`${item.name}-${originalIndex}`}
                    data-food-index={originalIndex}
                    onClick={() => toggleSelect(originalIndex)}
                    onDoubleClick={(e) => { e.preventDefault(); toggleOrdered(originalIndex); }}
                    className={`text-left rounded-3xl overflow-hidden bg-white border-2 shadow-sm transition-all duration-200 active:scale-[0.97] ${
                      isSelected
                        ? "border-primary shadow-primary/10 shadow-md"
                        : allergenDanger
                        ? "border-red-300"
                        : "border-gray-100"
                    }`}
                  >
                    {imageReady ? (
                      <div className="aspect-square bg-muted relative overflow-hidden">
                        <img
                          src={resolvedImageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />

                        {/* Selection checkmark */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" weight="bold" />
                          </div>
                        )}

                        {/* Ordered badge */}
                        {orderedDishIds.has(stableDishKey(item)) && (
                          <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 shadow-md">
                            <ForkKnife size={10} weight="fill" /> ordered
                          </div>
                        )}

                        {/* Allergen badge */}
                        {allergenDanger && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                            ⚠️ Allergen
                          </div>
                        )}

                        {/* Calorie badge */}
                        {getItemCalories(item) != null && (
                          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
                            {getItemCalories(item)} cal
                          </div>
                        )}

                        {/* Flag wrong image */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFlagImage(originalIndex);
                          }}
                          className={`absolute bottom-2 left-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${flaggedIndices.has(originalIndex) ? 'bg-red-500 text-white' : 'bg-black/50 text-white/80 hover:bg-red-500 hover:text-white'}`}
                          title="Wrong image?"
                        >
                          <Flag size={14} />
                        </button>
                      </div>
                    ) : (
                      // Wave B.1: category-styled placeholder while image generates.
                      // Cards look "done" the moment OCR returns names — no empty
                      // image area, no "generating image..." text gap.
                      <CategoryPlaceholder
                        name={item.name}
                        description={item.description}
                        loading={item.imageLoading}
                      />
                    )}

                    {/* Info */}
                    <div className={`p-3 ${!imageReady && sortMode !== "menu" ? "bg-gradient-to-br from-[#fff8e8] to-[#fff3da]" : ""}`}>
                      {!imageReady && sortMode !== "menu" && (
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-500/70 mb-2">yumz pick</p>
                      )}
                      <h4 className="font-semibold text-sm text-foreground leading-tight line-clamp-2">
                        {item.name}
                      </h4>
                      {item.originalName && item.originalName !== item.name && (
                        <p className="text-[10px] text-blue-500 italic mt-0.5 line-clamp-1">
                          {item.originalName}
                        </p>
                      )}
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                      {item.variantOptions && (
                        <p className="text-xs text-foreground/80 font-medium mt-1">
                          <span className="text-muted-foreground">🍽️ choose: </span>{item.variantOptions}
                        </p>
                      )}
                      {!imageReady && item.imageLoading && (
                        <p className="text-xs text-amber-700/70 mt-2">generating image...</p>
                      )}
                      {!imageReady && !item.imageLoading && (
                        <button
                          className="flex items-center gap-1 text-xs text-amber-600 mt-2 hover:text-amber-800 active:scale-95 transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageLoading: true } : p));
                            generateFoodImageDirect(item.name, item.description).then(result => {
                              const url = toBlobUrl(result.imageUrl) || result.imageUrl;
                              if (url) setImageMap(prev => ({ ...prev, [originalIndex]: url }));
                              setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageUrl: url || undefined, imageLoading: false } : p));
                            }).catch(() => {
                              setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageLoading: false } : p));
                            });
                          }}
                        >
                          <ArrowClockwise size={12} weight="bold" /> retry image
                        </button>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        {item.price && (
                          <span className="text-xs font-semibold text-primary">
                            {item.price}
                          </span>
                        )}
                        {item.dietaryTags && item.dietaryTags.length > 0 && (
                          <div className="flex gap-0.5">
                            {item.dietaryTags.slice(0, 2).map((tag) => (
                              <span key={tag} className="text-xs">
                                {tag.toLowerCase().includes("veg")
                                  ? "🌱"
                                  : tag.toLowerCase().includes("spicy")
                                  ? "🌶️"
                                  : tag.toLowerCase().includes("gluten")
                                  ? "🚫🌾"
                                  : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {visibleDrinkItems.length > 0 && (
                <>
                  <div className="col-span-2 flex items-center gap-3 pt-5 pb-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                    <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border border-primary/10 rounded-full">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary/70">
                        drinks
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                  </div>

                  {visibleDrinkItems.map(({ item, originalIndex }) => {
                    const resolvedImageUrl = imageMap[originalIndex] || item.imageUrl;
                    const imageReady = !!resolvedImageUrl;
                    const isSelected = selectedIndices.has(originalIndex);
                    const allergenDanger = hasAllergenConfig && hasAllergenMatch(item);

                    return (
                      <button
                        key={`drink-${item.name}-${originalIndex}`}
                        data-food-index={originalIndex}
                        onClick={() => toggleSelect(originalIndex)}
                        className={`text-left rounded-3xl overflow-hidden bg-white border-2 shadow-sm transition-all duration-200 active:scale-[0.97] ${
                          isSelected
                            ? "border-primary shadow-primary/10 shadow-md"
                            : allergenDanger
                            ? "border-red-300"
                            : "border-gray-100"
                        }`}
                      >
                        {imageReady ? (
                          <div className="aspect-square bg-muted relative overflow-hidden">
                            <img
                              src={resolvedImageUrl}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-lg">
                                <Check className="w-4 h-4 text-white" weight="bold" />
                              </div>
                            )}
                          </div>
                        ) : (
                          // Wave B.1: category placeholder for the drinks-list site too
                          <CategoryPlaceholder
                            name={item.name}
                            description={item.description}
                            loading={item.imageLoading}
                          />
                        )}

                        <div className={`p-3 ${!imageReady && sortMode !== "menu" ? "bg-gradient-to-br from-[#fff8e8] to-[#fff3da]" : ""}`}>
                          {!imageReady && (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-500/70 mb-2">drink</p>
                          )}
                          <h4 className="font-semibold text-sm text-foreground leading-tight line-clamp-2">
                            {item.name}
                          </h4>
                          {item.originalName && item.originalName !== item.name && (
                            <p className="text-[10px] text-blue-500 italic mt-0.5 line-clamp-1">
                              {item.originalName}
                            </p>
                          )}
                          {item.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                          {!imageReady && item.imageLoading && (
                            <p className="text-xs text-amber-700/70 mt-2">generating image...</p>
                          )}
                          {!imageReady && !item.imageLoading && (
                            <button
                              className="flex items-center gap-1 text-xs text-amber-600 mt-2 hover:text-amber-800 active:scale-95 transition-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageLoading: true } : p));
                                generateFoodImageDirect(item.name, item.description).then(result => {
                                  const url = toBlobUrl(result.imageUrl) || result.imageUrl;
                                  if (url) setImageMap(prev => ({ ...prev, [originalIndex]: url }));
                                  setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageUrl: url || undefined, imageLoading: false } : p));
                                }).catch(() => {
                                  setItems(prev => prev.map((p, i) => i === originalIndex ? { ...p, imageLoading: false } : p));
                                });
                              }}
                            >
                              <ArrowClockwise size={12} weight="bold" /> retry image
                            </button>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            {item.price && (
                              <span className="text-xs font-semibold text-primary">
                                {item.price}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
              </>
            )}

            {/* Dish count */}
            <p className="text-center text-xs text-muted-foreground pt-2 pb-4">
              {visibleWaiterItems.length} dishes
            </p>
          </div>

        </div>
      )}

      {/* ─── WAITER VIEW ─────────────────────────────────────────────── */}
      {viewState === "waiter" && (
        <div className="page-fade-in bg-white" style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
          <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-gray-100">
            <div className="safe-area-top px-4">
              <div className="flex items-center gap-3 h-11">
                <button onClick={() => setViewState("results")} className="p-1 -ml-1">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-lg font-bold flex items-center gap-1.5">your order <ForkKnife weight="bold" className="w-5 h-5" /></h1>
              </div>
            </div>
          </header>
          <main className="px-4 py-6 space-y-4 h-full overflow-y-auto overscroll-none pb-24">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold">{restaurantNameInput || "restaurant"}</h2>
              {location?.label && <p className="text-sm text-muted-foreground flex items-center justify-center gap-1"><MapPin weight="fill" className="w-3.5 h-3.5" /> {location.label}</p>}
            </div>
            {items.map((item, idx) => ({ item, idx })).filter(({ item, idx }) => selectedIndices.has(idx) && !item.sectionHeader).map(({ item, idx }, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-3xl">
                {(imageMap[idx] || item.imageUrl) && <img src={imageMap[idx] || item.imageUrl} className="w-16 h-16 rounded-2xl object-cover" />}
                <div className="flex-1">
                  <p className="font-bold text-lg">{item.name}</p>
                  {item.originalName && item.originalName !== item.name && (
                    <p className="text-base text-blue-600 font-medium">{item.originalName}</p>
                  )}
                </div>
                {item.price && <span className="font-bold text-lg">{item.price}</span>}
              </div>
            ))}
            <div className="border-t pt-4 text-center">
              <p className="text-sm text-muted-foreground">{Array.from(selectedIndices).filter((i) => !items[i]?.sectionHeader).length} items</p>
            </div>
          </main>
        </div>
      )}

      {/* ─── SHARING VIEW — Card + Caption ──────────── */}
      {viewState === "sharing" && (
        <div className="flex flex-col page-fade-in" style={{ position: 'fixed', inset: 0, background: '#0F0F0F', overflow: 'hidden' }}>
          {/* Header */}
          <div className="safe-area-top flex items-center justify-between px-4 pt-2 pb-1 relative z-10 flex-shrink-0">
            <button
              onClick={handleSkipSharing}
              className="p-2 rounded-2xl hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
            <div className="text-center">
              <p className="text-white font-bold text-sm">share your yumz</p>
              <p className="text-white/40 text-xs">@ {sharingRestaurant}</p>
            </div>
            <div className="w-9" />
          </div>

          {/* Card preview */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-2 min-h-0">
            {sharePreviewLoading ? (
              <div className="w-full max-w-xs aspect-[4/5] rounded-3xl bg-white/5 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : sharePreviewUrl ? (
              <img
                src={sharePreviewUrl}
                alt="Share card preview"
                className="w-full max-w-xs object-contain rounded-2xl shadow-2xl"
                style={{ maxHeight: '38vh' }}
              />
            ) : (
              <div className="w-full max-w-xs aspect-[4/5] rounded-3xl bg-white/5 flex items-center justify-center">
                <ForkKnife weight="light" className="w-12 h-12 text-white/20" />
              </div>
            )}

            {/* Camera icon centered below card — tap to add/change photo */}
            <button
              onClick={() => {
                groupCameraRef.current?.click();
                if (!groupPhoto) setShareMode("photo-card");
              }}
              className="mt-3 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-all hover:bg-white/20"
            >
              <Camera size={18} weight={groupPhoto ? "fill" : "regular"} className={groupPhoto ? "text-white" : "text-white/50"} />
            </button>
            {groupPhoto && (
              <p className="text-white/30 text-[10px] mt-1">photo added</p>
            )}

            {/* Caption preview */}
            <p className="text-white/60 text-sm text-center mt-2 px-4 italic leading-relaxed max-w-xs">
              {SHARE_TONES.find(t => t.id === shareTone)?.caption(sharingRestaurant || 'this place')}
            </p>
          </div>

          {/* Caption tone selector */}
          <div className="px-4 pb-2 flex-shrink-0">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest text-center mb-2">how was it?</p>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1 justify-center flex-wrap">
              {SHARE_TONES.map((t) => {
                const Icon = t.icon;
                const active = shareTone === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setShareTone(t.id)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      active
                        ? "shadow-lg scale-105"
                        : "bg-white/10 text-white/60 hover:bg-white/20"
                    }`}
                    style={active ? { backgroundColor: t.color, color: '#fff' } : undefined}
                  >
                    <Icon size={14} weight={active ? "fill" : "regular"} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Share action */}
          <div className="px-5 pb-2 flex-shrink-0">
            <button
              onClick={() => handleShareToSocial("generic")}
              disabled={!!sharingPlatform || sharePreviewLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-white/15 active:scale-95 transition-all disabled:opacity-50"
            >
              {sharingPlatform ? (
                <div className="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
              ) : (
                <ShareNetwork size={18} weight="bold" className="text-white" />
              )}
              <span className="text-sm font-bold text-white">Share</span>
            </button>
          </div>

          {/* Skip */}
          <div className="px-5 flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <button
              onClick={handleSkipSharing}
              className="w-full py-3 rounded-2xl bg-white/5 text-white/40 text-xs font-semibold active:scale-95 transition-all"
            >
              skip for now
            </button>
          </div>
        </div>
      )}

      {/* ─── EASY READ VIEW ──────────────────────────────────────────── */}
      {viewState === "easy-read" && (() => {
        const foodOnly = items.filter(it => !it.sectionHeader);
        const total = foodOnly.length;
        if (total === 0) return null;

        return (
          <div
            className="fixed inset-0 z-50 bg-black flex flex-col overflow-hidden"
          >
            {/* Top bar — floating over content */}
            <div className="absolute top-0 left-0 right-0 z-10 safe-area-top">
              <div className="flex items-center h-12 px-4">
                <button
                  onClick={() => setViewState("results")}
                  className="w-10 h-10 flex items-center justify-center active:scale-95 transition-transform"
                >
                  <X size={24} weight="bold" className="text-white drop-shadow-lg" />
                </button>
                <div className="flex-1 text-center">
                  <span className="text-xs font-semibold text-white/70 drop-shadow">
                    {easyReadIndex + 1} / {total}
                  </span>
                </div>
                <div className="w-10" />
              </div>
            </div>

            {/* Swipeable cards — snap scroll */}
            <div
              className="flex-1 overflow-y-scroll overflow-x-hidden snap-y snap-mandatory"
              style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}
              onScroll={(e) => {
                const el = e.currentTarget;
                const cardH = el.clientHeight;
                if (cardH > 0) {
                  const newIdx = Math.round(el.scrollTop / cardH);
                  if (newIdx !== easyReadIndex && newIdx >= 0 && newIdx < total) {
                    setEasyReadIndex(newIdx);
                  }
                }
              }}
            >
              {foodOnly.map((dish, i) => {
                const imgSrc = imageMap[items.indexOf(dish)] || dish.imageUrl;
                return (
                  <div
                    key={`tiktok-${i}`}
                    className="w-full snap-start snap-always flex flex-col relative"
                    style={{ height: "100dvh", minHeight: "100dvh" }}
                  >
                    {/* Dish image — top 55%, contain so full dish visible */}
                    <div className="relative w-full" style={{ height: '55%' }}>
                      {imgSrc ? (
                        <>
                          {/* Blurred background fill */}
                          <img
                            src={imgSrc}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60"
                          />
                          {/* Sharp dish image — fully visible */}
                          <img
                            src={imgSrc}
                            alt={dish.name}
                            className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl"
                          />
                        </>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
                      )}
                      {/* Fade into dark bottom */}
                      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black to-transparent" />
                    </div>

                    {/* Content — bottom 45%, scrollable if needed */}
                    <div className="flex-1 bg-black px-5 pt-2 pb-28 overflow-y-auto">
                      {/* Dish name */}
                      <h1 className="text-[28px] font-extrabold text-white leading-tight drop-shadow-lg">
                        {dish.name}
                      </h1>

                      {/* Original name */}
                      {dish.originalName && dish.originalName !== dish.name && (
                        <p className="text-base italic text-white/60 mt-1">{dish.originalName}</p>
                      )}

                      {/* Price + calories row */}
                      <div className="flex items-center gap-3 mt-2">
                        {dish.price && (
                          <span className="text-xl font-bold text-white">{dish.price}</span>
                        )}
                        {dish.nutrition?.calories && (
                          <span className="flex items-center gap-1 text-sm text-white/70">
                            <Fire size={14} weight="fill" className="text-orange-400" />
                            {dish.nutrition.calories} cal
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {dish.description && (
                        <p className="text-[15px] text-white/80 leading-relaxed mt-3 line-clamp-3">
                          {dish.description}
                        </p>
                      )}

                      {/* Variant / protein choices */}
                      {dish.variantOptions && (
                        <div className="mt-3 rounded-2xl bg-white/10 backdrop-blur-sm px-3 py-2">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-white/60 mb-0.5">choose your protein</p>
                          <p className="text-[14px] text-white/90 font-medium">{dish.variantOptions}</p>
                        </div>
                      )}

                      {/* Tags */}
                      {((dish.dietaryTags && dish.dietaryTags.length > 0) || (dish.allergens && dish.allergens.length > 0)) && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {dish.dietaryTags?.map((tag: string) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-500/30 text-green-200 backdrop-blur-sm">
                              {tag}
                            </span>
                          ))}
                          {dish.allergens?.map((a: string) => (
                            <span key={a} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/30 text-red-200 backdrop-blur-sm">
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pick dish button — fixed bottom-right */}
                    <div className="absolute right-4 bottom-28 z-10 flex flex-col items-center gap-1">
                      <button
                        onClick={() => {
                          const realIdx = items.indexOf(dish);
                          const newSelected = new Set(selectedIndices);
                          if (newSelected.has(realIdx)) newSelected.delete(realIdx);
                          else newSelected.add(realIdx);
                          setSelectedIndices(newSelected);
                        }}
                        className="w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md transition-all active:scale-90"
                        style={{ backgroundColor: selectedIndices.has(items.indexOf(dish)) ? "rgba(239,68,68,1)" : "rgba(255,255,255,0.2)" }}
                      >
                        <ForkKnife size={22} weight="fill" className="text-white" />
                      </button>
                      <span className="text-[10px] font-semibold text-white/70">
                        {selectedIndices.has(items.indexOf(dish)) ? "picked" : "pick"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom safe area */}
            <div className="safe-area-bottom bg-transparent" />
          </div>
        );
      })()}

      {/* Bottom nav — show on home and results views, hide when share card is open */}
      {(viewState === "home" || viewState === "results") && !showAnticipationShare && (
        <BottomNav
          onScanTap={() => { if (viewState === "home" && !showHome) { captureFrame(); } else { setShowHome(false); setViewState("home"); navigate("/"); } }}
          actions={viewState === "results" ? [
            {
              icon: ShareNetwork,
              label: "Share",
              variant: "default" as const,
              onClick: () => setShowAnticipationShare(true),
            },
            ...(selectedIndices.size > 0 ? [
              {
                icon: ForkKnife,
                label: "My Order",
                variant: "primary" as const,
                onClick: async () => {
                  const finalName = restaurantNameInput.trim() || restaurantName || "Unknown Restaurant";

                  const topVisibleFood = visibleWaiterItems
                    .filter(({ item }) => !item.sectionHeader && !isDrinkLike(item))
                    .slice(0, 2);

                  await Promise.allSettled(topVisibleFood.map(({ item, originalIndex }, order) => {
                    if (item.imageUrl) return Promise.resolve();
                    return new Promise<void>((resolve) => {
                      queueImageGeneration(
                        item.name,
                        item.description,
                        finalName,
                        originalIndex,
                        async (idx, url) => {
                          if (url && item.imageKey) await cacheImageByItemKey(item.imageKey, url);
                          setItems((prev) =>
                            prev.map((p, i) => i === idx ? { ...p, imageUrl: url || undefined, imageLoading: false } : p)
                          );
                          resolve();
                        },
                        3000 - order,
                        item.imageKey,
                      );
                      setTimeout(resolve, 3500);
                    });
                  }));

                  const entries = Array.from(selectedIndices).filter(idx => !items[idx]?.sectionHeader).map(idx => {
                    const item = items[idx];
                    return { id: generateId("dish"), menuId: "auto", name: item.name, description: item.description || "", variantOptions: item.variantOptions || undefined, price: item.price || "", imageUrl: item.imageUrl, dietaryTags: item.dietaryTags || [], originalName: item.originalName || item.name, detectedLanguage: item.detectedLanguage, allergens: item.allergens || [], currency: item.currency || "", nutrition: item.nutrition, restaurantName: finalName, orderedAt: new Date().toISOString(), location: location || undefined };
                  });
                  if (entries.length > 0) saveDishes(entries as any);
                  setSharingDishes(entries.map(d => ({ name: d.name, imageUrl: d.imageUrl })));
                  setSharingRestaurant(finalName);
                  setSharingLocation(location?.label);
                  setGroupPhoto(null);
                  setViewState("waiter");
                },
              },
              {
                icon: Check,
                label: "Done",
                variant: "muted" as const,
                onClick: () => {
                  const finalName = restaurantNameInput.trim() || restaurantName || "Unknown Restaurant";
                  const entries = Array.from(selectedIndices).filter(idx => !items[idx]?.sectionHeader).map(idx => {
                    const item = items[idx];
                    return { id: generateId("dish"), menuId: "auto", name: item.name, description: item.description || "", variantOptions: item.variantOptions || undefined, price: item.price || "", imageUrl: item.imageUrl, dietaryTags: item.dietaryTags || [], originalName: item.originalName || item.name, detectedLanguage: item.detectedLanguage, allergens: item.allergens || [], currency: item.currency || "", nutrition: item.nutrition, restaurantName: finalName, orderedAt: new Date().toISOString(), location: location || undefined };
                  });
                  if (entries.length > 0) saveDishes(entries as any);
                  setItems([]); setImageMap({}); setFlaggedIndices(new Set()); setSelectedIndices(new Set()); navigate("/my-dishes");
                },
              },
            ] : []),
            {
              icon: TextAa,
              label: "Swipe",
              variant: "accent" as const,
              onClick: () => { setEasyReadIndex(0); setViewState("easy-read"); },
            },
            {
              icon: ArrowLeft,
              label: "Back",
              variant: "muted" as const,
              onClick: () => setViewState("home"),
            },
          ] : undefined}
        />
      )}

      {/* Moment 1: Anticipation Share Card */}
      {showAnticipationShare && (
        <AnticipationShareCard
          restaurantName={restaurantNameInput.trim() || restaurantName || "Unknown Restaurant"}
          mood={null}
          orderedDishes={items.filter(it => !it.sectionHeader && orderedDishIds.has(stableDishKey(it)))}
          onClose={() => setShowAnticipationShare(false)}
          locationLabel={location?.label}
          onMoodSelected={(mood) => {
            if (currentMenuId) {
              updateMenu(currentMenuId, { mood });
            }
          }}
        />
      )}

      {/* Modals */}
      <AllergySettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSave={handleSettingsSave}
      />

      <RatingPrompt
        open={showRating}
        dishes={ratingDishes}
        onRate={handleRating}
        onClose={handleCloseRating}
      />
    </div>
  );
};

export default Index;
